import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  X, User, Phone, Briefcase, Mail, Shield, Clock, Building2, Hash,
  PhoneCall, Calendar, CreditCard, Save, Loader2, AlertCircle, Plus, Trash2,
  GraduationCap, Award, Upload, MapPin, Home,
} from 'lucide-react';

import tenant from '../../config/tenant';
import { RELATIONSHIP_OPTS } from '../../constants/relationshipOptions';
import { isMasterApproved } from '../../utils/masterApproval';
import { checkTinUnique, normaliseTin } from '../../utils/tinUnique';
import { NEW_BANK_IS_APPROVED, isBankActive, bankActiveFlag, isBankPrimary } from '../../utils/employeeBankApproval';
import IsoDateInput from '../../components/common/Isodateinput';
import SearchableSelect from '../../components/common/SearchableSelect';
import ExperienceInput from '../../components/common/Experienceinput';
import {
  sanitiseEpf, checkEpf,
  sanitiseDecimal,
  sanitiseNic, normaliseNic, checkNic,
  checkDateOfBirth, maxDobIso,
  checkDateOfJoining, todayIso,
  sanitisePhone, checkPhone, checkMobile,
  sanitisePostal,
  checkEmail,
  parseExperience, checkExperience,
  toIso,
} from '../../utils/employeeValidation';
import { getEmployeeProfilePicUrl } from '../../utils/employeeUtils';
import employeeService from '../../services/EmployeeService';
import { clearAuthedFileCache } from '../../utils/authedFile';
import bankService from '../../services/BankService';
import bankBranchService from '../../services/BankBranchService';
import designationService from '../../services/DesignationService';
import departmentService from '../../services/DepartmentService';
import divisionService from '../../services/DivisionService';
import locationService from '../../services/LocationService';
import gradeService from '../../services/GradeService';
import shiftService from '../../services/ShiftService';
import employeeCategoryService from '../../services/EmployeeCategoryService';
import { saveEmployeeImage } from '../../store/slices/imageSlice';
import { getLoggedInServiceNo, getLoggedInUserId } from '../../utils/masterDataUtils';
import {
  CONTACT_TYPE_CONTACT, CONTACT_TYPE_EMERGENCY, isEmergencyContact, contactTypeFor,
} from '../../utils/contactType';
import regionService from '../../services/RegionService';
import { RG, rgFilterDistricts, rgFilterElectorates, rgStationOptions, isContractCategory } from '../../utils/employeeUtils';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (err) => reject(err);
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const d = (v) => {
  if (v === null || v === undefined || v === '' || v === 'null' || v === 'undefined') return null;
  const s = String(v).trim();
  return s === '' || s === '0' ? null : s;
};

const fmtDate = (v) => {
  const raw = d(v);
  if (!raw) return '';
  const iso = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  try {
    const dt = new Date(v);
    if (isNaN(dt.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
  } catch { return ''; }
};

// Is the category currently picked on the form "Contract"?
// Employee Category dropdown: only categories with HEC_IS_ACTIVE true
// (also accepts "True" / 1 in case the API sends them that way).
// Division dropdown: HDD_CURRENT_STATUS "A" only.
const isDivisionActive = (r) => String(r?.HDD_CURRENT_STATUS ?? '').trim().toUpperCase() === 'A';

// Department dropdown: HDD_IS_ACTIVE true only.
const isDeptActive = (r) => {
  const v = r?.HDD_IS_ACTIVE;
  if (v === undefined || v === null) return true;
  return v === true || v === 1 || /^(true|1)$/i.test(String(v).trim());
};

const isCategoryActive = (c) => {
  const v = c?.HEC_IS_ACTIVE;
  if (v === undefined || v === null) return true;   // field missing → don't hide it
  return v === true || v === 1 || /^(true|1)$/i.test(String(v).trim());
};

const isContractForm = (form, categories = []) =>
  !!form && isContractCategory(
    categories.find((c) => String(c.value) === String(form.HED_EMPLOYMENT_CATEGORY_ID))?.label
      || form.HEC_CATEGORY_NAME
  );

const RELIGION_OPTS = [
  { value: 'B', label: 'Buddhism' }, { value: 'C', label: 'Christian' },
  { value: 'H', label: 'Hindu' }, { value: 'I', label: 'Islam' },
  { value: 'O', label: 'Other' },
];
const MARITAL_OPTS = [
  { value: 'S', label: 'Single' }, { value: 'M', label: 'Married' },
  { value: 'D', label: 'Divorced' }, { value: 'W', label: 'Widowed' },
];
const GENDER_OPTS = [{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }];
const BLOOD_OPTS = [
  { value: '1', label: 'A+' }, { value: '2', label: 'A-' }, { value: '3', label: 'B+' },
  { value: '4', label: 'B-' }, { value: '5', label: 'AB+' }, { value: '6', label: 'AB-' },
  { value: '7', label: 'O+' }, { value: '8', label: 'O-' },
];

const STATUS_OPTS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

/* Sent as p_InactiveReason — the reason TEXT itself, not an ID. */
const INACTIVE_REASONS = [
  'Resignation',
  'Termination',
  'Retirement',
  'Contract Completed',
  'Absconded / Absent Without Notice',
  'Dismissal',
  'Probation Not Confirmed',
  'Redundancy',
  'Transfer',
  'Deceased',
  'Other',
];
const INACTIVE_REASON_OPTS = INACTIVE_REASONS.map((r) => ({ value: r, label: r }));

const OT_TYPES = [
  {
    value: "Fixed",
    label: "Fixed"
  },
  {
    value: "Variable",
    label: "Variable"
  }
]

const EMPLOYEE_TYPES = [
  {
    value: "Field",
    label: "Field Staff"
  },
  {
    value: "Staff",
    label: "Office Staff"
  }
];


const resolveInactiveReason = (v) => {
  const raw = String(v ?? '').trim();
  if (!raw) return '';
  const asIndex = Number(raw);
  if (Number.isInteger(asIndex) && asIndex >= 1 && asIndex <= INACTIVE_REASONS.length) {
    return INACTIVE_REASONS[asIndex - 1];
  }
  return raw;
};

function calcYears(date) {
  if (!date) return null;
  const s = new Date(date);
  if (isNaN(s.getTime())) return null;
  const now = new Date();
  let y = now.getFullYear() - s.getFullYear();
  if (now.getMonth() - s.getMonth() < 0 ||
     (now.getMonth() - s.getMonth() === 0 && now.getDate() < s.getDate())) y--;
  return y >= 0 ? y : null;
}

function SH({ label, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-2 mt-4 first:mt-0">
      {Icon && <Icon size={12} className="text-primary-600" />}
      <span className="text-[10px] font-bold text-primary-700 uppercase tracking-wider">{label}</span>
      <div className="flex-1 h-px bg-primary-100" />
    </div>
  );
}

/* A native <select> silently falls back to its first option when the value
   matches none of them — so a saved region would read as blank while the code
   sat in state. This keeps the current value visible by adding an option for
   it, labelled with whatever name the record carried. */
function withCurrent(options, value, label) {
  if (!value) return options;
  if (options.some((o) => String(o.value) === String(value))) return options;
  return [{ value: String(value), label: label || String(value) }, ...options];
}

/* A type-to-search dropdown for the long master lists — the Region levels run
   to hundreds of rows, where a plain <select> means scrolling. Same behaviour
   as the Add form's Sel: portal-rendered so it escapes the modal's overflow,
   search box only once the list is worth filtering. */
function SearchSelect({ value, onChange, options = [], placeholder = '—', className = '', searchable = false }) {
  // Thin wrapper over the shared SearchableSelect. Search shows once the list
  // has more than 5 options, or always when `searchable` is set.
  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={options}
      emptyOption={{ value: '', label: placeholder }}
      placeholder={placeholder}
      searchMinOptions={searchable ? 0 : 6}
      className={`${className} appearance-none bg-white`}
    />
  );
}

function FI({ label, value, onChange, type = 'text', options, mono = false, error, placeholder, render, searchable = false, loading = false }) {
  /* One field shape across the app: 36px tall, rounded-lg, gray-200 border,
     primary focus — matching the Add form and the master-file panels. */
  const base = `w-full h-9 text-xs rounded-lg border px-2.5 outline-none transition-colors
    ${error ? 'border-red-300 bg-red-50 focus:border-red-400'
            : 'border-gray-200 bg-white text-gray-800 focus:border-primary-500'}
    ${mono ? 'font-mono' : ''}`;

  return (
    <div className="py-1.5">
      <label className="text-[11px] text-gray-500 font-medium block mb-1">{label}</label>
      {/* `render` lets a custom control (date picker, experience boxes) sit in
          the same label + error frame as a plain input. */}
      {render ? render(base) : options && searchable ? (
        /* Type-to-search dropdown. Values come back as strings, exactly as the
           native <select> gave them, so saving and the change check behave the same. */
        <SearchSelect
          className={base}
          searchable
          value={value ?? ''}
          options={options}
          placeholder={loading ? 'Loading...' : (placeholder || '—')}
          onChange={(v) => onChange(v === '' || v === null || v === undefined ? '' : String(v))}
        />
      ) : options ? (
        <select className={base} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input
          className={base}
          type={type}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {error && <p className="text-[10px] text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}

// ─── Tabs ──────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'overview',   label: 'Overview',   icon: User },
  { key: 'contact',    label: 'Contact',    icon: Phone },
  { key: 'employment', label: 'Employment', icon: Briefcase },
  { key: 'bank',       label: 'Bank',       icon: CreditCard },
  { key: 'qualify',    label: 'Education',  icon: GraduationCap },
];

/* Same single brand green as the employee profile card, so opening Edit
   doesn't change the colour of the screen you were just looking at. */
const HEADER_GRADIENT = 'bg-gradient-to-br from-primary-600 to-primary-700';

// ─── Build form state ──────────────────────────────────────────────────────
function buildFormState(e) {
  /* Region lives on the permanent address row. Read whichever spelling the
     API returns; blank when the employee has no address yet. */
  const allAddresses = e.Addresses || e._Addresses || [];
  const ofType = (t) =>
    allAddresses.find((a) => String(a.HEA_ADDRESS_TYPE || '').toUpperCase() === t) || {};
  const permanent = ofType('P');
  const current = ofType('C');
  /* Region is read from the permanent row, falling back to the current one and
     then to whatever address exists — an employee whose only row is type 'C'
     used to show an empty Region section. */
  const regionRow = Object.keys(permanent).length ? permanent
    : (Object.keys(current).length ? current : (allAddresses[0] || {}));
  const heaVal = (keys) => {
    for (const k of keys) if (regionRow[k] != null && String(regionRow[k]).trim() !== '') return String(regionRow[k]).trim();
    return '';
  };
  /* Address lines come back either split (…LINE1/2/3) or as one LINE column,
     depending on the endpoint. Read both so nothing is lost on the way in. */
  const addrVal = (row, keys) => {
    for (const k of keys) if (row[k] != null && String(row[k]).trim() !== '') return String(row[k]).trim();
    return '';
  };

  return {
    // Region (new section on the Contact tab)
    RG_PROVINCE:       heaVal(['HEA_PROVINCE_ID', 'HEA_PROVINCE_CODE', 'PROVINCE_CODE', 'HPD_PROVINCE_CODE']),
    RG_DISTRICT:       heaVal(['HEA_DISTRICT_ID', 'HEA_DISTRICT_CODE', 'DISTRICT_CODE', 'HDD_DISTRICT_CODE']),
    RG_ELECTORATE:     heaVal(['HEA_ELECTORATE_CODE', 'HEA_ELECTORATE_ID', 'ELECTORATE_CODE']),
    RG_POLICE_STATION: heaVal(['HEA_STAION_CODE', 'HEA_STATION_CODE', 'HEA_POLICE_STATION_ID', 'HPD_STATION_CODE']),
    RG_GRAMA:          heaVal(['HEA_GRAMA_CODE', 'HEA_GRAMASEWAKA_CODE', 'GRAMASEWAKA_CODE',
                               'HGD_GRAMASEWAKADIVISION_CODE']),

    /* The names the list endpoint joins in. Used to recover the code when the
       row carries the name but not the id — see the effect that backfills
       RG_* once the master lists have loaded. */
    RG_PROVINCE_NAME:   heaVal(['HPD_PROVINCE_NAME', 'PROVINCE_NAME']),
    RG_DISTRICT_NAME:   heaVal(['HDD_DISTRICT_NAME', 'DISTRICT_NAME']),
    RG_ELECTORATE_NAME: heaVal(['ELECTORATE_AREA', 'ELECTORATE_NAME']),
    RG_STATION_NAME:    heaVal(['HPD_POLICESTATION_NAME', 'POLICESTATION_NAME', 'STATION_NAME']),
    RG_GRAMA_NAME:      heaVal(['HGD_GRAMASEWAKADIVISION_NAME', 'HGD_GRAMASEWAKA_DIVISION_NAME',
                                'GRAMASEWAKA_DIVISION_NAME', 'DIVISION_NAME']),

    /* Addresses. An employee with no address row at all still gets empty
       fields here, so one can be created from this form — previously there
       was nothing to type into and the address stayed null forever. */
    PERM_ADDRESS_ID:   addrVal(permanent, ['HEA_ADDRESS_ID', 'HeaAddressId', 'AddressId']),
    PERM_LINE1:        addrVal(permanent, ['HEA_ADDRESS_LINE1', 'HEA_ADDRESS_LINE']),
    PERM_LINE2:        addrVal(permanent, ['HEA_ADDRESS_LINE2']),
    PERM_LINE3:        addrVal(permanent, ['HEA_ADDRESS_LINE3']),
    PERM_CITY:         addrVal(permanent, ['HEA_CITY']),
    PERM_POSTAL:       addrVal(permanent, ['HEA_POSTAL_CODE']),

    CURR_ADDRESS_ID:   addrVal(current, ['HEA_ADDRESS_ID', 'HeaAddressId', 'AddressId']),
    CURR_LINE1:        addrVal(current, ['HEA_ADDRESS_LINE1', 'HEA_ADDRESS_LINE']),
    CURR_LINE2:        addrVal(current, ['HEA_ADDRESS_LINE2']),
    CURR_LINE3:        addrVal(current, ['HEA_ADDRESS_LINE3']),
    CURR_CITY:         addrVal(current, ['HEA_CITY']),
    CURR_POSTAL:       addrVal(current, ['HEA_POSTAL_CODE']),

    // Personal
    HED_TITLE:            d(e.HED_TITLE) || d(e.p_title) || '',
    HED_FIRST_NAME:       d(e.HED_FIRST_NAME) || d(e.p_first_name) || '',
    HED_MIDDLE_NAME:      d(e.HED_MIDDLE_NAME) || '',
    HED_LAST_NAME:        d(e.HED_LAST_NAME) || d(e.p_last_name) || '',
    HED_REPORT_NAME:      d(e.HED_REPORT_NAME) || d(e.p_report_name) || '',
    HED_INITIALS:         d(e.HED_INITIALS) || d(e.p_initials) || '',
    HED_GENDER:           d(e.HED_GENDER) || d(e.p_sex) || '',
    HED_DATE_OF_BIRTH:    fmtDate(e.HED_DATE_OF_BIRTH || e.p_birth_date),
    HED_MARITAL_STATUS:   d(e.HED_MARITAL_STATUS) || d(e.p_marital_status) || '',
    HED_RELIGION:         d(e.HED_RELIGION) || d(e.p_religion) || '',
    HED_BLOOD_GROUP:      d(e.HED_BLOOD_GROUP) || d(e.p_blood_group) || '',
    // 0 is a real answer, so only null/undefined become an empty field.
    HED_NO_OF_CHILDREN:   e.HED_NO_OF_CHILDREN === null || e.HED_NO_OF_CHILDREN === undefined
                            ? '' : String(e.HED_NO_OF_CHILDREN),
    HED_DISTANCE:         d(e.HED_DISTANCE) || d(e.p_Distance) || d(e.Distance) || '',

    // Identification
    HED_NIC_NO:           d(e.HED_NIC_NO) || d(e.p_nid) || '',
    HED_EPF_NO:           d(e.HED_EPF_NO) || d(e.p_epf) || '',
    HED_ETF_NO:           d(e.HED_ETF_NO) || '',
    HED_TIN_NO:           d(e.HED_TIN_NO) || d(e.p_TinNo) || '',
    HED_PASSPORT_NO:      d(e.HED_PASSPORT_NO) || d(e.p_passport) || '',

    // Contact
    HED_MOBILE_NO:        d(e.HED_MOBILE_NO) || d(e.p_mobile) || '',
    HED_HOME_NO:          d(e.HED_HOME_NO) || d(e.p_home_tel) || '',
    HED_OFFICE_NO:        d(e.HED_OFFICE_NO) || d(e.p_office_tel) || '',
    HED_PERSONAL_EMAIL:   d(e.HED_PERSONAL_EMAIL) || d(e.p_personal_email) || '',
    HED_COMPANY_EMAIL:    d(e.HED_COMPANY_EMAIL) || d(e.p_email) || '',

    // Employment
    HED_DATE_OF_JOINING:  fmtDate(e.HED_DATE_OF_JOINING || e.p_recruitment_date),
    HED_RECRUITMENT_DATE: fmtDate(e.HED_RECRUITMENT_DATE),
    HED_PROMOTED_DATE:    fmtDate(e.HED_PROMOTED_DATE),
    HED_PERMANENT_DATE:   fmtDate(e.HED_PERMANENT_DATE),
    HED_RETIREMENT_DATE:  fmtDate(e.HED_RETIREMENT_DATE),
    HED_TERMINATION_DATE: fmtDate(e.HED_TERMINATION_DATE),
    HED_CONFIRMATION_DATE: fmtDate(e.HED_CONFIRMATION_DATE || e.p_permanent_date),
    HED_DESIGNATION_NAME: d(e.HED_DESIGNATION_NAME) || d(e.p_designation) || '',
    HDD_DEPARTMENT_NAME:  d(e.HDD_DEPARTMENT_NAME) || d(e.p_department_code) || '',
    HED_COSTCENTER:       d(e.HED_COSTCENTER) || d(e.p_location_code) || '',

    HED_OT_TYPE:           d(e.HED_OT_TYPE) || d(e.p_OT_Type) || d(e.OT_TYPE) || '',
    HED_EMPLOYEE_TYPE:     d(e.HED_EMPLOYEE_TYPE) || d(e.p_Employee_Type) || d(e.EMPLOYEE_TYPE) || '',

    HED_NOTICE_PERIOD_DAYS: d(e.HED_NOTICE_PERIOD_DAYS) || '',
    HED_OT_RATE:          d(e.HED_OT_RATE) || '',
    HED_IS_ACTIVE: e.HED_IS_ACTIVE !== undefined
      ? Boolean(e.HED_IS_ACTIVE)
      : (!e.p_inactive_type || e.p_inactive_type === '' || e.p_inactive_type === '0'),
    // Only meaningful while the employee is inactive; cleared on reactivation.
    HED_LEAVING_REASON:  resolveInactiveReason(d(e.HED_LEAVING_REASON) || d(e.p_inactive_type)),
    HED_LEAVING_DATE:    toIso(d(e.HED_LEAVING_DATE)),
    HED_REMARKS:          d(e.HED_REMARKS) || d(e.p_remarks) || '',
    HED_TOTAL_EXPERIANCE: d(e.HED_TOTAL_EXPERIANCE) || '',
    /* Kept as a string so it matches the <select> option values; the diff
       compares it against the record as a string too. */
    HED_REPORTING_EMPLOYEE_ID: String(
      d(e.HED_REPORTING_EMPLOYEE_ID) || d(e.HedReportingEmployeeId) ||
      d(e.ReportingEmployeeId) || d(e.reportingEmployeeId) || ''
    ),

    // Contacts ('Contact' or 'Emergency'). Records saved before contact types
    // existed have no type and were all emergency contacts, so they stay that way.
    Contacts: (e.Contacts || []).map((c, i) => ({
      _key: c.HEC_CONTACT_ID || `c-${i}`,
      HEC_CONTACT_ID: c.HEC_CONTACT_ID,
      HEC_CONTACT_NAME: d(c.HEC_CONTACT_NAME) || '',
      HEC_CONTACT_NUMBER: d(c.HEC_CONTACT_NUMBER) || '',
      HEC_RELATIONSHIP: d(c.HEC_RELATIONSHIP) || '',
      HEC_ADDRESS: d(c.HEC_ADDRESS) || '',
      HEC_CONTACT_TYPE: contactTypeFor(isEmergencyContact(d(c.HEC_CONTACT_TYPE) || CONTACT_TYPE_EMERGENCY)),
      _isNew: false,
      _isDeleted: false,
    })),
    // Bank accounts
    Banks: (e.Banks || []).map((b, i) => ({
      _key: b.HEB_EMPLOYEE_BANK_ID || `b-${i}`,
      HEB_EMPLOYEE_BANK_ID: b.HEB_EMPLOYEE_BANK_ID,
      HBD_BANK_NAME: d(b.HBD_BANK_NAME) || '',
      HBB_BRANCH_NAME: d(b.HBB_BRANCH_NAME) || '',
      HEB_ACCOUNT_NAME: d(b.HEB_ACCOUNT_NAME) || '',
      HEB_ACCOUNT_NUMBER: d(b.HEB_ACCOUNT_NUMBER) || '',
      HEB_ACCOUNT_TYPE: d(b.HEB_ACCOUNT_TYPE) || 'Savings',
      // "True"/"False" text from the API — Boolean() would read "False" as true.
      HEB_IS_PRIMARY: isBankPrimary(b),
      // From HEB_IS_ACTIVE; sent back as p_hebIsActive (1 / 0).
      _isActive: isBankActive(b),
      // HBD_BANK_ID: d(b.HBD_BANK_ID) || '',
      // HBB_BRANCH_ID: d(b.HBB_BRANCH_ID) || '',
      HBD_BANK_ID: d(b.HEB_BANK_ID) || '',
      HBB_BRANCH_ID: d(b.HEB_BRANCH_ID) || '',
      _isNew: false,
      _isDeleted: false,
    })),
    // Qualifications
    Qualifications: (e.Qualifications || []).map((q, i) => ({
      _key: q.HEQ_QUALIFICATION_ID || `q-${i}`,
      HEQ_QUALIFICATION_ID: q.HEQ_QUALIFICATION_ID,
      HEQ_QUALIFICATION_NAME: d(q.HEQ_QUALIFICATION_NAME) || '',
      HEQ_FIELD_OF_STUDY: d(q.HEQ_FIELD_OF_STUDY) || '',
      HEQ_INSTITUTE: d(q.HEQ_INSTITUTE) || '',
      HEQ_RESULT: d(q.HEQ_RESULT) || '',
      HEQ_GRADE: d(q.HEQ_GRADE) || '',
      HEQ_GPA: d(q.HEQ_GPA) || '',
      HEQ_QUALIFICATION_TYPE_ID: d(q.HEQ_QUALIFICATION_TYPE_ID) || 1,
      _isNew: false,
      _isDeleted: false,
    })),

    // Employment-related dropdown IDs
    HED_DESIGNATION_ID: d(e.HED_DESIGNATION_ID) || '',
    HDD_DEPARTMENT_ID: d(e.HED_DEPARTMENT_ID) || d(e.HedDepartmentId) || d(e.DepartmentId) || d(e.departmentId) || d(e.HDD_DEPARTMENT_ID) || d(e.p_hedDepartmentId) || d(e.HDD_DEPARTMENT_CODE) || d(e.HDD_DEP_ID) || d(e.HDD_DEP_CODE) || d(e.p_department_code) || '', 
    HED_DIV_CODE: d(e.HED_DIV_CODE) || d(e.HDD_DIV_CODE) || '',
    HDD_DEPARTMENT_NAME: d(e.HDD_DEPARTMENT_NAME) || d(e.HDD_DEP_NAME) || d(e.p_department_name) || d(e.p_department_code) || '',
    HED_LOCATION_ID: d(e.HED_LOCATION_ID) || d(e.HLD_LOCATION_ID) || d(e.HLD_LOC_ID) || d(e.p_location_id) || d(e.p_location_code) || d(e.HED_COSTCENTER) || '',
    HED_GRADE_ID: d(e.HED_GRADE_ID) || '',
    HED_SHIFT_ID: d(e.HED_SHIFT_ID) || '',
    HED_EMPLOYMENT_CATEGORY_ID: d(e.HED_EMPLOYMENT_CATEGORY_ID) || d(e.HedEmploymentCategoryId) || d(e.EmploymentCategoryId) || d(e.employmentCategoryId) || d(e.p_hedEmploymentCategoryId) || d(e.HEC_EMPLOYMENT_CATEGORY_ID) || '',
    HEC_CATEGORY_NAME: d(e.HEC_CATEGORY_NAME) || d(e.EmploymentCategoryName) || d(e.employmentCategoryName) || d(e.p_cat_name) || d(e.p_cat_code) || '',
  };
}

// ─── Validation ────────────────────────────────────────────────────────────
function validate(form) {
  const errs = {};
  if (!form.HED_FIRST_NAME.trim()) errs.HED_FIRST_NAME = 'First name is required';
  if (!form.HED_LAST_NAME.trim())  errs.HED_LAST_NAME = 'Last name is required';

  // Employment details the Add form also requires.
  const blank = (v) => !String(v ?? '').trim();
  if (blank(form.HED_DIV_CODE))               errs.HED_DIV_CODE = 'Division is required';
  if (blank(form.HDD_DEPARTMENT_ID))          errs.HDD_DEPARTMENT_ID = 'Department is required';
  if (blank(form.HED_LOCATION_ID))            errs.HED_LOCATION_ID = 'Location is required';
  if (blank(form.HED_GRADE_ID))               errs.HED_GRADE_ID = 'Grade is required';
  if (blank(form.HED_EMPLOYMENT_CATEGORY_ID)) errs.HED_EMPLOYMENT_CATEGORY_ID = 'Employee category is required';
  if (blank(form.HED_SHIFT_ID))               errs.HED_SHIFT_ID = 'Shift is required';

  // Only one primary bank account. Some employees already have several —
  // untick the extras (each one is saved with p_hebIsPrimary = false).
  const primaryBanks = (form.Banks || []).filter((b) => !b._isDeleted && b.HEB_IS_PRIMARY);
  if (primaryBanks.length > 1) {
    errs.BANK_PRIMARY = `${primaryBanks.length} bank accounts are marked primary — only one can be. Untick the others.`;
  }

  /* The same rules the Add form applies — see utils/employeeValidation.js.
     Both screens write to the same columns, so they have to agree. */
  const dobErr = checkDateOfBirth(form.HED_DATE_OF_BIRTH);
  if (dobErr) errs.HED_DATE_OF_BIRTH = dobErr;

  const epfErr = checkEpf(form.HED_EPF_NO);
  if (epfErr) errs.HED_EPF_NO = epfErr;

  const nicErr = checkNic(form.HED_NIC_NO, { required: false });
  if (nicErr) errs.HED_NIC_NO = nicErr;

  const dojErr = checkDateOfJoining(form.HED_DATE_OF_JOINING, { required: false });
  if (dojErr) errs.HED_DATE_OF_JOINING = dojErr;

  const personalEmailErr = checkEmail(form.HED_PERSONAL_EMAIL, 'Personal email');
  if (personalEmailErr) errs.HED_PERSONAL_EMAIL = personalEmailErr;

  const companyEmailErr = checkEmail(form.HED_COMPANY_EMAIL, 'Company email');
  if (companyEmailErr) errs.HED_COMPANY_EMAIL = companyEmailErr;

  // Mobile is required and must be exactly 10 numbers.
  const mobileErr = checkMobile(form.HED_MOBILE_NO, 'Mobile no');
  if (mobileErr) errs.HED_MOBILE_NO = mobileErr;
  const homeErr = checkPhone(form.HED_HOME_NO, 'Home phone');
  if (homeErr) errs.HED_HOME_NO = homeErr;
  const officeErr = checkPhone(form.HED_OFFICE_NO, 'Office phone');
  if (officeErr) errs.HED_OFFICE_NO = officeErr;

  const expParts = parseExperience(form.HED_TOTAL_EXPERIANCE);
  const expErr = checkExperience(expParts.years, expParts.months);
  if (expErr) errs.HED_TOTAL_EXPERIANCE = expErr;

  /* The region is stored on an address row, so it has nowhere to go unless at
     least one address is filled in. */
  const regionPicked = !!(form.RG_PROVINCE || form.RG_DISTRICT ||
                          form.RG_ELECTORATE || form.RG_POLICE_STATION ||
                          form.RG_GRAMA);
  const addressFilled = [form.PERM_LINE1, form.PERM_CITY, form.CURR_LINE1, form.CURR_CITY]
    .some((v) => String(v || '').trim() !== '');
  if (regionPicked && !addressFilled) {
    errs.PERM_LINE1 = 'Add a permanent or current address — the region is saved on the address';
  }

  // Emergency contact numbers follow the same rule.
  (form.Contacts || []).forEach((c, i) => {
    if (c._isDeleted) return;
    const err = checkPhone(c.HEC_CONTACT_NUMBER, 'Contact number');
    if (err) errs[`contact_${i}_number`] = err;
  });

  /* An inactive employee needs both a reason and a leaving date, and the date
     can't be in the future — nobody has left yet on a day that hasn't come. */
  if (!form.HED_IS_ACTIVE) {
    if (!String(form.HED_LEAVING_DATE || '').trim()) {
      errs.HED_LEAVING_DATE = 'Select the leaving date';
    } else {
      const leftErr = checkDateOfJoining(form.HED_LEAVING_DATE, { required: false });
      if (leftErr) errs.HED_LEAVING_DATE = leftErr.replace('Date of joining', 'Leaving date');
    }
  }
  if (!form.HED_IS_ACTIVE && !String(form.HED_LEAVING_REASON || '').trim())
    errs.HED_LEAVING_REASON = 'Select a reason for marking this employee inactive';
  return errs;
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function EmployeeEditModal({
  employee,
  isOpen,
  onClose,
  onSave,
  saving = false,
  saveError = null,
}) {
  const dispatch = useDispatch();
  const images = useSelector((s) => s.images?.images || {});
  const [activeTab, setActiveTab] = useState('overview');
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [localSaving, setLocalSaving] = useState(false);
  const [localSaveError, setLocalSaveError] = useState(null);

  // ─── File state for documents ──────────────────────────────────────────────
  const [files, setFiles] = useState({
    profilePic: null,
    nicDocument: null,
    birthCertificate: null,
    qualificationCertificate: null,
    traineeCertificate: null,
    otherCertificate: null,
    jobDescription: null,
  });

  const setFile = (key, file) => {
    setFiles(f => ({ ...f, [key]: file }));
    setDirty(true);
  };

  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);

  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);   // { value, label, div }
  const [divisions, setDivisions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [grades, setGrades] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reportingOptions, setReportingOptions] = useState([]);
  const [employmentLoading, setEmploymentLoading] = useState(false);

  const FIELD_MAP = {
    HED_TITLE:              'p_hedTitle',
    HED_FIRST_NAME:         'p_hedFirstName',
    HED_MIDDLE_NAME:        'p_hedMiddleName',
    HED_LAST_NAME:          'p_hedLastName',
    HED_REPORT_NAME:        'p_hedReportName',
    HED_INITIALS:           'p_hedInitials',
    HED_GENDER:             'p_hedGender',
    HED_DATE_OF_BIRTH:      'p_hedDateOfBirth',
    HED_MARITAL_STATUS:     'p_hedMaritalStatus',
    HED_RELIGION:           'p_hedReligion',
    HED_BLOOD_GROUP:        'p_hedBloodGroup',
    HED_NO_OF_CHILDREN:     'p_NoOfChildren',
    HED_DISTANCE:           'p_Distance',
    HED_NIC_NO:             'p_hedNicNo',
    HED_EPF_NO:             'p_hedEpfNo',
    HED_ETF_NO:             'p_hedEtfNo',
    HED_TIN_NO:             'p_TinNo',
    HED_PASSPORT_NO:        'p_hedPassportNo',
    // Region is deliberately NOT here: it lives on the address row, so it is
    // sent with UpdateEmployeeAddress / AddEmployeeAddress rather than in the
    // employee diff (matching AddEmployeeForm / employeeImport).
    HED_MOBILE_NO:          'p_hedMobileNo',
    HED_HOME_NO:            'p_hedHomeNo',
    HED_OFFICE_NO:          'p_hedOfficeNo',
    HED_PERSONAL_EMAIL:     'p_hedPersonalEmail',
    HED_COMPANY_EMAIL:      'p_hedCompanyEmail',
    HED_DATE_OF_JOINING:    'p_hedDateOfJoining',
    HED_RECRUITMENT_DATE:   'p_RecruitmentDate',
    HED_PROMOTED_DATE:      'p_PromotedDate',
    HED_PERMANENT_DATE:     'p_PermanentDate',
    HED_RETIREMENT_DATE:    'p_RetirementDate',
    HED_TERMINATION_DATE:   'p_termination_date',
    HED_CONFIRMATION_DATE:  'p_hedConfirmationDate',
    // HED_DESIGNATION_NAME:   'p_hedDesignationName',
    HDD_DEPARTMENT_ID:    'p_hedDepartmentId',
    HED_DIV_CODE:         'p_hddDivCode',
    HED_DESIGNATION_ID:     'p_hedDesignationId',
    HED_LOCATION_ID:        'p_hedLocationId',
    HED_GRADE_ID:           'p_hedGradeId',
    HED_SHIFT_ID:           'p_hedShiftId',
    HED_EMPLOYMENT_CATEGORY_ID:     'p_hedEmploymentCategoryId',

    HED_COSTCENTER:         'p_hedCostCenter',
    HED_NOTICE_PERIOD_DAYS: 'p_hedNoticePeriodDays',
    HED_OT_RATE:            'p_OT',

    HED_OT_TYPE:            'p_OT_Type',
    HED_EMPLOYEE_TYPE:      'p_Employee_Type',

    HED_IS_ACTIVE:          'p_hedIsActive',
    HED_LEAVING_REASON:    'p_hedLeavingReason',
    HED_LEAVING_DATE:      'p_hedLeavingDate',
    HED_REMARKS:            'p_hedRemarks',
    HED_TOTAL_EXPERIANCE:   'p_hedTotalExperiance',
    HED_REPORTING_EMPLOYEE_ID: 'p_hedReportingEmployeeId',
  };

  const [rgProvinces, setRgProvinces]     = useState([]);
  const [rgDistricts, setRgDistricts]     = useState([]);
  const [rgElectorates, setRgElectorates] = useState([]);
  const [rgStations, setRgStations]       = useState([]);
  const [rgGramas, setRgGramas]           = useState([]);

  // Region master data, fetched when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    const rows = (r) => (Array.isArray(r?.data?.ResultSet) ? r.data.ResultSet : []);
    Promise.all([
      regionService.GetProvinceList().catch(() => null),
      regionService.GetDistrictList().catch(() => null),
      regionService.GetElectorateList().catch(() => null),
      regionService.GetPoliceStationList().catch(() => null),
      regionService.GetGramaList().catch(() => null),
    ]).then(([p, d, e2, st, gr]) => {
      if (!alive) return;
      setRgProvinces(rows(p));
      setRgDistricts(rows(d));
      setRgElectorates(rows(e2));
      setRgStations(rows(st));
      setRgGramas(rows(gr));
    });
    return () => { alive = false; };
  }, [isOpen]);

  /* Some rows come back with the region NAME joined in but no id — the
     dropdowns are keyed by code, so the field would sit empty with the value
     sitting right there in the record. Once the master lists have loaded, any
     level that has a name but no code is matched back to its code. */
  useEffect(() => {
    if (!isOpen) return;
    if (!rgProvinces.length && !rgDistricts.length && !rgElectorates.length &&
        !rgStations.length && !rgGramas.length) return;

    const norm = (v) => String(v ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
    const codeFor = (list, name, codeOf, nameOf) => {
      if (!name) return '';
      const hit = list.find((r) => norm(nameOf(r)) === norm(name));
      return hit ? String(codeOf(hit)) : '';
    };

    setForm((f) => {
      if (!f) return f;
      const next = { ...f };
      let changed = false;
      const fill = (codeKey, nameKey, list, codeOf, nameOf) => {
        if (next[codeKey] || !next[nameKey]) return;
        const code = codeFor(list, next[nameKey], codeOf, nameOf);
        if (code) { next[codeKey] = code; changed = true; }
      };
      fill('RG_PROVINCE',       'RG_PROVINCE_NAME',   rgProvinces,   RG.provCode, RG.provName);
      fill('RG_DISTRICT',       'RG_DISTRICT_NAME',   rgDistricts,   RG.distCode, RG.distName);
      fill('RG_ELECTORATE',     'RG_ELECTORATE_NAME', rgElectorates, RG.elecCode, RG.elecArea);
      fill('RG_POLICE_STATION', 'RG_STATION_NAME',    rgStations,    RG.stnCode,  RG.stnName);
      fill('RG_GRAMA',          'RG_GRAMA_NAME',      rgGramas,      RG.gramaCode, RG.gramaName);
      return changed ? next : f;
    });
    // Backfilling is not a user edit, so `dirty` is deliberately left alone.
  }, [isOpen, rgProvinces, rgDistricts, rgElectorates, rgStations, rgGramas]);

  useEffect(() => {
    if (isOpen && employee) {
      setForm(buildFormState(employee));
      setErrors({});
      setDirty(false);
      setActiveTab('overview');
      // Reset file inputs when modal opens
      setFiles({ profilePic: null, nicDocument: null, birthCertificate: null, qualificationCertificate: null, traineeCertificate: null, otherCertificate: null, jobDescription: null });
    }
  }, [isOpen, employee]);

  const handleKey = useCallback((ev) => {
    if (ev.key === 'Escape' && !saving && !localSaving) onClose();
  }, [onClose, saving, localSaving]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKey]);

  const set = (key) => (val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setDirty(true);
  };

  /* Status and its reason move together: going Active clears any stale reason,
     so we never send an inactive reason for an active employee. */
  const setStatus = (val) => {
    const isActive = val === 'active';
    setForm((f) => ({
      ...f,
      HED_IS_ACTIVE: isActive,
      HED_LEAVING_REASON: isActive ? '' : f.HED_LEAVING_REASON,
      HED_LEAVING_DATE: isActive ? '' : f.HED_LEAVING_DATE,
    }));
    setDirty(true);
  };

  // ─── Contact helpers ─────────────────────────────────────────────────────
  const setContact = (idx, key) => (val) => {
    setForm((f) => {
      const list = [...f.Contacts];
      list[idx] = { ...list[idx], [key]: val };
      return { ...f, Contacts: list };
    });
    setDirty(true);
  };

  const addContact = () =>
    setForm((f) => ({
      ...f,
      Contacts: [
        ...f.Contacts,
        {
          _key: `new-${Date.now()}`,
          HEC_CONTACT_NAME: '',
          HEC_CONTACT_NUMBER: '',
          HEC_RELATIONSHIP: '',
          HEC_ADDRESS: '',
          HEC_CONTACT_TYPE: CONTACT_TYPE_CONTACT,
          _isNew: true,
          _isDeleted: false,
        },
      ],
    }));

  const removeContact = (idx) => {
    const contact = form.Contacts[idx];
    if (contact.HEC_CONTACT_ID) {
      setForm((f) => {
        const list = [...f.Contacts];
        list[idx] = { ...list[idx], _isDeleted: true };
        return { ...f, Contacts: list };
      });
    } else {
      setForm((f) => ({ ...f, Contacts: f.Contacts.filter((_, i) => i !== idx) }));
    }
    setDirty(true);
  };

  // ─── Bank helpers ────────────────────────────────────────────────────────
  const setBank = (idx, key) => (val) => {
    setForm((f) => {
      const list = [...f.Banks];
      list[idx] = { ...list[idx], [key]: val };
      return { ...f, Banks: list };
    });
    setDirty(true);
  };

  const addBank = () =>
    setForm((f) => ({
      ...f,
      Banks: [
        ...f.Banks,
        {
          _key: `new-${Date.now()}`,
          HBD_BANK_NAME: '',
          HBB_BRANCH_NAME: '',
          HEB_ACCOUNT_NAME: '',
          HEB_ACCOUNT_NUMBER: '',
          HEB_ACCOUNT_TYPE: 'Savings',
          HEB_IS_PRIMARY: false,
          HBD_BANK_ID: '',
          HBB_BRANCH_ID: '',
          _isNew: true,
          _isDeleted: false,
        },
      ],
    }));

  const removeBank = (idx) => {
    const bank = form.Banks[idx];
    if (bank.HEB_EMPLOYEE_BANK_ID) {
      setForm((f) => {
        const list = [...f.Banks];
        list[idx] = { ...list[idx], _isDeleted: true };
        return { ...f, Banks: list };
      });
    } else {
      setForm((f) => ({ ...f, Banks: f.Banks.filter((_, i) => i !== idx) }));
    }
    setDirty(true);
  };

  const extractList = (res) => {
    if (!res) return [];

    const data = res?.data ?? res;

    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.ResultSet)) return data.ResultSet;
    if (Array.isArray(data.resultSet)) return data.resultSet;
    if (Array.isArray(data.Result)) return data.Result;
    if (Array.isArray(data.result)) return data.result;
    if (Array.isArray(data.Data)) return data.Data;
    if (Array.isArray(data.data)) return data.data;

    return [];
  };

  const pick = (obj, hints, fallback = '') => {
    if (!obj || typeof obj !== 'object') return fallback;

    for (const h of hints) {
      if (
        obj[h] !== undefined &&
        obj[h] !== null &&
        obj[h] !== ''
      ) {
        return obj[h];
      }
    }

    const objKeys = Object.keys(obj).map(k => ({
      raw: k,
      norm: String(k)
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase(),
    }));

    for (const h of hints) {
      const hNorm = String(h)
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase();

      if (!hNorm) continue;

      let match = objKeys.find(k => k.norm === hNorm);

      if (!match) {
        match = objKeys.find(k => k.norm.endsWith(hNorm));
      }

      if (!match && hNorm.length >= 3) {
        match = objKeys.find(k => k.norm.includes(hNorm));
      }

      if (
        match &&
        obj[match.raw] !== undefined &&
        obj[match.raw] !== null &&
        obj[match.raw] !== ''
      ) {
        return obj[match.raw];
      }
    }

    return fallback;
  };

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadBankData = async () => {
      setBankLoading(true);

      try {
        const [
          designationRes,
          departmentRes,
          locationRes,
          gradeRes,
          shiftRes,
          categoryRes,
          bankRes, 
          branchRes,
          reportingRes,
          divisionRes,
        ] = await Promise.all([
          designationService.GetDesignationList().catch(() => null),
          departmentService.GetDepartmentList().catch(() => null),
          locationService.GetLocationList().catch(() => null),
          gradeService.GetGradeList().catch(() => null),
          shiftService.GetShiftList().catch(() => null),
          employeeCategoryService.GetEmployeeCategoryList().catch(() => null),
          bankService.GetBankList().catch(() => null),
          bankBranchService.getBankBranchList().catch(() => null),
          employeeService.GetEmployeeList().catch(() => null),
          divisionService.GetDivisions().catch(() => null),
        ]);

        if (cancelled) return;

        console.log('[EmployeeEditModal] GetBanks response:', bankRes);
        console.log('[EmployeeEditModal] GetAllBranch response:', branchRes);

        const designationList = extractList(designationRes);
        // Active departments only; the employee's current one is kept so the
        // field isn't left blank if it has since been made inactive.
        const currentDeptId = String(employee?.HED_DEPARTMENT_ID ?? '');
        const departmentList = extractList(departmentRes).filter((r) =>
          isDeptActive(r) ||
          (currentDeptId && String(r.HDD_DEPARTMENT_ID ?? '') === currentDeptId));
        const deptDiv = new Map(departmentList.map((r) =>
          [String(r.HDD_DEPARTMENT_ID ?? ''), String(r.HDD_DIV_CODE ?? '').trim()]));

        // Active divisions (HDD_CURRENT_STATUS "A"), shown by HDD_DIV_DESC; the
        // employee's current division is kept even if it is no longer active.
        const currentDiv = String(employee?.HED_DIV_CODE ?? '').trim();
        setDivisions(extractList(divisionRes)
          .filter((r) => isDivisionActive(r) || (currentDiv && String(r.HDD_DIV_CODE ?? '').trim() === currentDiv))
          .map((r) => ({ value: String(r.HDD_DIV_CODE ?? '').trim(), label: String(r.HDD_DIV_DESC || r.HDD_DIV_CODE || '').trim() }))
          .filter((o) => o.value));

        // Older records with a department but no division: take the
        // department's division so the two dropdowns line up.
        if (!currentDiv) {
          setForm((f) => (f.HED_DIV_CODE || !f.HDD_DEPARTMENT_ID ? f
            : { ...f, HED_DIV_CODE: deptDiv.get(String(f.HDD_DEPARTMENT_ID)) || '' }));
        }
        const locationList = extractList(locationRes);
        const gradeList = extractList(gradeRes);
        const shiftList = extractList(shiftRes);
        // Active categories only. The employee's current category is kept even
        // if it has since been made inactive, so the field isn't left blank.
        const currentCategoryId = String(employee?.HED_EMPLOYMENT_CATEGORY_ID ?? '');
        const categoryList = extractList(categoryRes).filter((c) =>
          isCategoryActive(c) ||
          (currentCategoryId && String(c.HEC_EMPLOYMENT_CATEGORY_ID ?? '') === currentCategoryId));

        // Only approved master records (HBD_IS_APPROVED / HBB_IS_APPROVED = "1")
        // are selectable — a bank still awaiting approval must not be assignable.
        const bankList = extractList(bankRes).filter(isMasterApproved);
        const branchList = extractList(branchRes).filter(isMasterApproved);

        console.log('[EmployeeEditModal] bankList:', bankList);
        console.log('[EmployeeEditModal] branchList:', branchList);

        const bankOptions = bankList
          .map((b) => {
            const id = pick(b, [
              'HBD_BANK_ID',
              'BankId',
              'bankId',
              'BankID',
              'BankCode',
            ]);

            const name = pick(b, [
              'HBD_BANK_NAME',
              'BankName',
              'bankName',
              'BankDesc',
            ]);

            return id
              ? {
                  value: String(id),
                  label: String(name || id),
                }
              : null;
          })
          .filter(Boolean);

        const branchOptions = branchList
          .map((b) => {
            const id = pick(b, [
              'HBB_BRANCH_ID',
              'BranchId',
              'branchId',
              'BranchID',
              'BranchCode',
            ]);

            const name = pick(b, [
              'HBB_BRANCH_NAME',
              'BranchName',
              'branchName',
              'BranchDesc',
            ]);

            const code = pick(b, [
              'HBB_BRANCH_CODE',
              'BranchCode',
              'branchCode',
            ]);

            const bankId = pick(b, [
              'HBB_BANK_ID',
              'BankId',
              'bankId',
              'BankID',
            ]);

            return id
              ? {
                  value: String(id),
                  label: `${name || id}${code ? ` (${code})` : ''}`,
                  bankId: String(bankId),
                }
              : null;
          })
          .filter(Boolean);

        console.log('[EmployeeEditModal] bankOptions:', bankOptions);
        console.log('[EmployeeEditModal] branchOptions:', branchOptions);

        setBanks(bankOptions);
        setBranches(branchOptions);

        /* Reporting officer — the same "Name (STAFFID)" label the Add form
           shows. The employee being edited is left out: nobody reports to
           themselves. */
        const selfId = String(
          employee?.HED_EMPLOYEE_ID ?? employee?.hedEmployeeId ?? employee?.EmployeeId ?? ''
        );
        setReportingOptions(
          extractList(reportingRes).map((r) => {
            const id = pick(r, ['HED_EMPLOYEE_ID', 'HedEmployeeId', 'EmployeeId', 'hedEmployeeId', 'employeeId', 'Id', 'id']);
            if (!id || String(id) === selfId) return null;
            const svcNo = pick(r, ['HED_SERVICE_NO', 'HedServiceNo', 'ServiceNo', 'serviceNo', 'p_ced_service_no']);
            const reportName = pick(r, ['HED_REPORT_NAME', 'HedReportName', 'ReportName', 'reportName', 'p_report_name']);
            const first = pick(r, ['HED_FIRST_NAME', 'HedFirstName', 'FirstName', 'firstName', 'p_first_name']);
            const last = pick(r, ['HED_LAST_NAME', 'HedLastName', 'LastName', 'lastName', 'p_last_name']);
            const name = reportName || `${first} ${last}`.trim() || String(id);
            return { value: String(id), label: `${name}${svcNo ? ` (${svcNo})` : ''}` };
          }).filter(Boolean)
        );

        setDesignations(
          toOpts(
            designationList,
            [
              'DesignationId',
              'designationId',
              'HDS_DESIGNATION_ID',
              'HDS_DESIGNATION_CODE',
              'p_des_code',
              'DesignationCode',
            ],
            [
              'DesignationName',
              'designationName',
              'HDS_DESIGNATION_NAME',
              'HDS_DESIGNATION_DESC',
              'p_des_name',
              'DesignationDesc',
            ]
          )
        );
        setDepartments(
          toOpts(
            departmentList,
            [
              'DepartmentId',
              'departmentId',
              'HDD_DEPARTMENT_ID',
              'HDD_DEP_ID',
              'HDD_DEP_CODE',
              'p_department_code',
              'DepartmentCode',
              'code',
              'id',
            ],
            [
              'DepartmentName',
              'departmentName',
              'HDD_DEPARTMENT_NAME',
              'HDD_DEP_NAME',
              'HDD_DEP_DESC',
              'p_department_name',
              'DepartmentDesc',
              'name',
              'description',
            ]
          ).map((o) => ({ ...o, div: deptDiv.get(String(o.value)) || '' }))
        );
        setLocations(
          toOpts(
            locationList,
            [
              'LocationId',
              'locationId',
              'HLD_LOCATION_ID',
              'HLD_LOC_ID',
              'HLD_LOC_CODE',
              'p_location_code',
              'p_loc_code',
            ],
            [
              'LocationName',
              'locationName',
              'HLD_LOCATION_NAME',
              'HLD_LOC_NAME',
              'HLD_LOC_DESC',
              'p_location_name',
              'p_loc_name',
            ]
          )
        );

        setGrades(
          toOpts(
            gradeList,
            [
              'GradeId',
              'gradeId',
              'HGC_GRADE_ID',
              'HGC_GRADE_CODE',
              'p_grade_code',
              'p_initial_grade',
            ],
            [
              'GradeName',
              'gradeName',
              'HGC_GRADE_NAME',
              'HGC_GRADE_DESC',
              'p_grade_name',
            ]
          )
        );

        setShifts(
          toOpts(
            shiftList,
            [
              'ShiftId',
              'shiftId',
              'HSC_SHIFT_ID',
              'HSC_SHIFT_CODE',
              'p_shift_code',
            ],
            [
              'ShiftName',
              'shiftName',
              'HSC_SHIFT_NAME',
              'HSC_SHIFT_DESC',
              'p_shift_name',
            ]
          )
        );

        setCategories(
          toOpts(
            categoryList,
            [
              'HEC_EMPLOYMENT_CATEGORY_ID',
              'CategoryId',
              'categoryId',
              'HEC_CATEGORY_ID',
            ],
            [
              'HEC_CATEGORY_NAME',
              'CategoryName',
              'categoryName',
              'HEC_CATEGORY_DESC',
              'p_cat_name',
            ]
          )
        );

      } catch (err) {
        console.error(
          '[EmployeeEditModal] Failed to load bank/branch data:',
          err
        );

        setBanks([]);
        setBranches([]);
      } finally {
        if (!cancelled) {
          setBankLoading(false);
        }
      }
    };

    loadBankData();

    return () => {
      cancelled = true;
    };
    // The employee id is a dependency because the reporting-officer list
    // excludes whoever is being edited.
  }, [isOpen, employee?.HED_EMPLOYEE_ID]);

  // -------------- Dropdown helper --------------------
  const toOpts = (list, idKeys = [], nameKeys = []) => {
    if (!Array.isArray(list)) return [];

    return list
      .map((row) => {
        if (!row || typeof row !== 'object') return null;

        const id = pick(row, [
          ...idKeys,
          'Id',
          'id',
          'Code',
          'code',
          'ID',
          'KEY',
          'key',
        ]);

        const name = pick(row, [
          ...nameKeys,
          'Name',
          'name',
          'Description',
          'description',
          'Desc',
          'desc',
          'Title',
          'title',
          'LABEL',
          'label',
        ]);

        if (id === undefined || id === null || id === '') {
          return null;
        }

        return {
          value: String(id),
          label: String(name || id),
        };
      })
      .filter(Boolean);
  };

  // ─── Qualification helpers ──────────────────────────────────────────────
  const setQualification = (idx, key) => (val) => {
    setForm((f) => {
      const list = [...f.Qualifications];
      list[idx] = { ...list[idx], [key]: val };
      return { ...f, Qualifications: list };
    });
    setDirty(true);
  };

  const addQualification = () =>
    setForm((f) => ({
      ...f,
      Qualifications: [
        ...f.Qualifications,
        {
          _key: `new-${Date.now()}`,
          HEQ_QUALIFICATION_NAME: '',
          HEQ_FIELD_OF_STUDY: '',
          HEQ_INSTITUTE: '',
          HEQ_RESULT: '',
          HEQ_GRADE: '',
          HEQ_GPA: '',
          HEQ_QUALIFICATION_TYPE_ID: 1,
          _isNew: true,
          _isDeleted: false,
        },
      ],
    }));

  const removeQualification = (idx) => {
    const q = form.Qualifications[idx];
    if (q.HEQ_QUALIFICATION_ID) {
      setForm((f) => {
        const list = [...f.Qualifications];
        list[idx] = { ...list[idx], _isDeleted: true };
        return { ...f, Qualifications: list };
      });
    } else {
      setForm((f) => ({ ...f, Qualifications: f.Qualifications.filter((_, i) => i !== idx) }));
    }
    setDirty(true);
  };

  // ─── Save Handler ────────────────────────────────────────────────────────
  const handleSave = async () => {
    const errs = validate(form);
    // Contract staff must have a Termination Date.
    if (isContractForm(form, categories) && !d(form.HED_TERMINATION_DATE)) {
      errs.HED_TERMINATION_DATE = 'Termination date is required for Contract employees';
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      if (errs.HED_FIRST_NAME || errs.HED_LAST_NAME || errs.HED_NIC_NO ||
          errs.HED_EPF_NO || errs.HED_DATE_OF_BIRTH) setActiveTab('overview');
      else if (errs.HED_MOBILE_NO || errs.HED_HOME_NO || errs.HED_OFFICE_NO ||
               errs.HED_PERSONAL_EMAIL || errs.HED_COMPANY_EMAIL || errs.PERM_LINE1 ||
               Object.keys(errs).some((k) => k.startsWith('contact_'))) setActiveTab('contact');
      else if (errs.HED_DATE_OF_JOINING || errs.HED_TOTAL_EXPERIANCE || errs.HED_TERMINATION_DATE ||
               errs.HED_DIV_CODE || errs.HDD_DEPARTMENT_ID || errs.HED_LOCATION_ID || errs.HED_GRADE_ID ||
               errs.HED_EMPLOYMENT_CATEGORY_ID || errs.HED_SHIFT_ID) setActiveTab('employment');
      else if (errs.BANK_PRIMARY) setActiveTab('bank');
      return;
    }

    setLocalSaving(true);
    const serviceNo = employee.HED_SERVICE_NO || employee.p_ced_service_no;
    const employeeId = employee.HED_EMPLOYEE_ID || employee.hedEmployeeId || employee.EmployeeId;

    const originalTin = employee.HED_TIN_NO || employee.p_TinNo || '';
    if (normaliseTin(form.HED_TIN_NO) !== normaliseTin(originalTin)) {
      try {
        const tinErr = await checkTinUnique(form.HED_TIN_NO, employeeId);
        if (tinErr) {
          setErrors((er) => ({ ...er, HED_TIN_NO: tinErr }));
          setActiveTab('overview');
          setLocalSaving(false);
          return;
        }
      } catch (err) {
        setLocalSaveError('Could not check whether the TIN No is already used. Please try again.');
        setLocalSaving(false);
        return;
      }
    }

    try {
      // ── 1. Update employee fields ──
      const diff = {};
      const normalize = (v) => (v === null || v === undefined || v === '') ? null : v;

      diff.p_hedServiceNo = serviceNo;

      const ID_KEYS = new Set(['HED_REPORTING_EMPLOYEE_ID']);

      for (const [formKey, apiKey] of Object.entries(FIELD_MAP)) {
        const originalVal = normalize(employee[formKey]);
        const newVal = normalize(form[formKey]);
        if (ID_KEYS.has(formKey)) {
          if (String(originalVal ?? '') !== String(newVal ?? '')) {
            diff[apiKey] = newVal ? Number(newVal) : null;
          }
          continue;
        }
        if (originalVal !== newVal) {
          let valueToSend = form[formKey];
          if (formKey === 'HED_IS_ACTIVE') {
            // API expects a real boolean here — true / false, not 1 / 0.
            valueToSend = Boolean(valueToSend);
          }
          diff[apiKey] = valueToSend;
        }
      }

      // Division always goes with the update, alongside the department.
      if (String(form.HED_DIV_CODE ?? '').trim()) diff.p_hddDivCode = String(form.HED_DIV_CODE).trim();

      // ETF carries the same number as EPF. The loop above only sends fields
      // that actually changed, and HED_ETF_NO never does on its own, so it has
      // to be attached whenever EPF is in the diff.
      if (diff.p_hedEpfNo !== undefined) {
        diff.p_hedEtfNo = diff.p_hedEpfNo;
      }

      // A typed lowercase v is stored and sent as a capital V.
      if (diff.p_hedNicNo !== undefined) {
        diff.p_hedNicNo = normaliseNic(diff.p_hedNicNo);
      }

      // Termination Date belongs to Contract staff only. Moved off Contract →
      // clear any saved termination date; otherwise don't send it.
      if (!isContractForm(form, categories)) {
        if (d(employee.HED_TERMINATION_DATE)) diff.p_termination_date = null;
        else delete diff.p_termination_date;
      }

      /* Reactivating clears the leaving details on the server too — otherwise
         an active employee keeps a leaving date from a previous spell. */
      if (diff.p_hedIsActive === true) {
        diff.p_hedLeavingDate = '';
        diff.p_hedLeavingReason = '';
      }

      if (Object.keys(diff).length > 1) {
        await employeeService.UpdateEmployee(diff);
      }

      /* ── 1b. Addresses ──────────────────────────────────────────────────
         An employee may have no address row at all, in which case there is
         nothing to update — the row has to be created first. Same parameter
         names as the Add form's AddEmployeeAddress call, so both paths write
         the row the same way. */
      /* Region is an attribute of one address row. It rides on the permanent
         address when there is one, otherwise on the current. */
      const permFilled = [form.PERM_LINE1, form.PERM_LINE2, form.PERM_LINE3, form.PERM_CITY, form.PERM_POSTAL]
        .some((v) => String(v || '').trim() !== '');
      const currFilled = [form.CURR_LINE1, form.CURR_LINE2, form.CURR_LINE3, form.CURR_CITY, form.CURR_POSTAL]
        .some((v) => String(v || '').trim() !== '');
      const regionCarrier = permFilled ? 'P' : (currFilled ? 'C' : null);
      const regionFields = {
        p_heaProvinceId:     form.RG_PROVINCE || null,
        p_heaDistrictId:     form.RG_DISTRICT || null,
        p_heaElectorateCode: form.RG_ELECTORATE || null,
        // "Staion" is the backend's spelling — kept verbatim.
        p_heaStaionCode:     form.RG_POLICE_STATION || null,
        p_heaGramaCode:      form.RG_GRAMA || null,
      };

      const saveAddress = async (type, id, line1, line2, line3, city, postal) => {
        const hasAny = [line1, line2, line3, city, postal].some((v) => String(v || '').trim() !== '');
        if (!hasAny) return;                       // nothing typed, nothing to write

        const fields = {
          p_hedEmployeeId:   employeeId,
          p_heaAddressType:  type,
          p_heaAddressLine1: line1 || '',
          p_heaAddressLine2: line2 || '',
          p_heaAddressLine3: line3 || '',
          p_heaCity:         city || '',
          p_heaPostalCode:   postal || '',
          p_isActive:        'A',
          p_remarks:         '',
          ...(regionCarrier === type ? regionFields : {}),
        };

        try {
          if (id) {
            await employeeService.UpdateEmployeeAddress({ p_heaAddressId: id, ...fields });
          } else {
            await employeeService.AddEmployeeAddress({ ...fields, p_heaCreatedBy: getLoggedInServiceNo() });
          }
        } catch (err) {
          console.error(`[EmployeeAddress ${type}] failed:`, err?.response?.data || err);
        }
      };

      await saveAddress('P', form.PERM_ADDRESS_ID, form.PERM_LINE1, form.PERM_LINE2,
                        form.PERM_LINE3, form.PERM_CITY, form.PERM_POSTAL);
      await saveAddress('C', form.CURR_ADDRESS_ID, form.CURR_LINE1, form.CURR_LINE2,
                        form.CURR_LINE3, form.CURR_CITY, form.CURR_POSTAL);

      // ── 2. Handle Contacts ──
      const contactsToSave = form.Contacts.filter((c) => !c._isDeleted);

      for (const contact of contactsToSave) {
        if (contact.HEC_CONTACT_ID && !contact._isNew) {
          await employeeService.UpdateEmployeeContact({
            p_hecContactId: contact.HEC_CONTACT_ID,
            p_hedEmployeeId: employeeId,
            p_hecContactName: contact.HEC_CONTACT_NAME || 'N/A',
            p_hecContactNumber: contact.HEC_CONTACT_NUMBER || '',
            p_hecRelationship: contact.HEC_RELATIONSHIP || 'Other',
            p_hecContactType: contact.HEC_CONTACT_TYPE || CONTACT_TYPE_CONTACT,
            p_hecAddress: contact.HEC_ADDRESS || '',
          });
        } else if (contact._isNew && (contact.HEC_CONTACT_NAME || contact.HEC_CONTACT_NUMBER)) {
          await employeeService.AddEmployeeContact({
            p_hedEmployeeId: employeeId,
            p_hecContactName: contact.HEC_CONTACT_NAME || 'N/A',
            p_hecContactNumber: contact.HEC_CONTACT_NUMBER || '',
            p_hecRelationship: contact.HEC_RELATIONSHIP || 'Other',
            p_hecContactType: contact.HEC_CONTACT_TYPE || CONTACT_TYPE_CONTACT,
            p_hecAddress: contact.HEC_ADDRESS || '',
            p_hecCreatedBy: getLoggedInServiceNo(),
            p_hecIsActive: true,
            p_hecRemarks: '',
          });
        }
      }

      const contactsToDelete = form.Contacts.filter((c) => c._isDeleted && c.HEC_CONTACT_ID);
      for (const contact of contactsToDelete) {
        try {
          await employeeService.DeleteEmployeeContact(contact.HEC_CONTACT_ID);
        } catch (err) {
          console.error('Failed to delete contact:', err);
        }
      }

      // ── 3. Handle Banks ──
      const banksToSave = form.Banks.filter((b) => !b._isDeleted);

      /* Saving the employee used to call UpdateEmployeeBank for every account,
         even untouched ones — and because an update always sends
         p_isApproved = 0, that quietly un-approved an already-approved account
         whenever anyone edited an unrelated field. So compare each account with
         the record it was loaded from and skip the call when nothing changed. */
      const originalBankById = {};
      (employee.Banks || []).forEach((b) => {
        originalBankById[String(b.HEB_EMPLOYEE_BANK_ID)] = b;
      });

      const same = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();

      const bankChanged = (bank) => {
        const original = originalBankById[String(bank.HEB_EMPLOYEE_BANK_ID)];
        if (!original) return true;   // no record to compare against — send it
        return !(
          same(bank.HBD_BANK_ID, original.HEB_BANK_ID) &&
          same(bank.HBB_BRANCH_ID, original.HEB_BRANCH_ID) &&
          same(bank.HEB_ACCOUNT_NAME, original.HEB_ACCOUNT_NAME) &&
          same(bank.HEB_ACCOUNT_NUMBER, original.HEB_ACCOUNT_NUMBER) &&
          same(bank.HEB_ACCOUNT_TYPE, original.HEB_ACCOUNT_TYPE) &&
          Boolean(bank.HEB_IS_PRIMARY) === isBankPrimary(original) &&
          Boolean(bank._isActive) === isBankActive(original)
        );
      };

      for (const bank of banksToSave) {
        if (bank.HEB_EMPLOYEE_BANK_ID && !bank._isNew) {
          // Untouched account: leave it exactly as it is, approval and all.
          if (!bankChanged(bank)) continue;

          /* Making an account inactive sends only the id, the employee and
             p_hebIsActive = 0 — nothing else on the account is touched. */
          if (bank._isActive === false) {
            const original = originalBankById[String(bank.HEB_EMPLOYEE_BANK_ID)];
            const primaryChanged = !original || Boolean(bank.HEB_IS_PRIMARY) !== isBankPrimary(original);
            await employeeService.UpdateEmployeeBank({
              p_hebEmployeeBankId: bank.HEB_EMPLOYEE_BANK_ID,
              p_hedEmployeeId: employeeId,
              p_hebIsActive: bankActiveFlag(false),
              // Only when the Primary tick was changed on this account.
              ...(primaryChanged ? { p_hebIsPrimary: Boolean(bank.HEB_IS_PRIMARY) } : {}),
            });
            continue;
          }

          await employeeService.UpdateEmployeeBank({
            p_hebEmployeeBankId: bank.HEB_EMPLOYEE_BANK_ID,
            p_hedEmployeeId: employeeId,
            // The bank and branch pickers were being dropped from the update,
            // so changing either one silently kept the old ids. The form holds
            // them as HBD_BANK_ID / HBB_BRANCH_ID (mapped from HEB_BANK_ID /
            // HEB_BRANCH_ID when the record loads).
            p_hebBankId: bank.HBD_BANK_ID ? Number(bank.HBD_BANK_ID) : null,
            p_hebBranchId: bank.HBB_BRANCH_ID ? Number(bank.HBB_BRANCH_ID) : null,
            p_hebAccountName: bank.HEB_ACCOUNT_NAME || '',
            p_hebAccountNumber: bank.HEB_ACCOUNT_NUMBER || '',
            p_hebAccountType: bank.HEB_ACCOUNT_TYPE || 'Savings',
            p_hebIsPrimary: Boolean(bank.HEB_IS_PRIMARY), // true = primary, false = not primary
            p_hebIsActive: bankActiveFlag(bank._isActive),
            p_hebCreatedBy: getLoggedInServiceNo(),
            // Edited details go back to pending. Only the Approve tick in the
            // employee modal ever sends 1.
            p_isApproved: NEW_BANK_IS_APPROVED,
          });
        } else if (bank._isNew && (bank.HEB_ACCOUNT_NUMBER || bank.HBD_BANK_NAME)) {
          await employeeService.AddBankDetails({
            p_hedEmployeeId: employeeId,
            p_hebBankId: bank.HBD_BANK_ID ? Number(bank.HBD_BANK_ID) : null,
            p_hebBranchId: bank.HBB_BRANCH_ID ? Number(bank.HBB_BRANCH_ID) : null,
            p_hebAccountName: bank.HEB_ACCOUNT_NAME || 'N/A',
            p_hebAccountNumber: bank.HEB_ACCOUNT_NUMBER || '',
            p_hebAccountType: bank.HEB_ACCOUNT_TYPE || 'Savings',
            p_hebIsPrimary: bank.HEB_IS_PRIMARY ? 1 : 0,
            p_hebCreatedBy: getLoggedInServiceNo(),
            p_isApproved: NEW_BANK_IS_APPROVED,
          });
        }
      }

      const banksToDelete = form.Banks.filter((b) => b._isDeleted && b.HEB_EMPLOYEE_BANK_ID);
      for (const bank of banksToDelete) {
        try {
          await employeeService.DeleteEmployeeBank(bank.HEB_EMPLOYEE_BANK_ID);
        } catch (err) {
          console.error('Failed to delete bank:', err);
        }
      }

      // ── 4. Handle Qualifications ──
      const qualsToSave = form.Qualifications.filter((q) => !q._isDeleted);

      for (const q of qualsToSave) {
        if (q.HEQ_QUALIFICATION_ID && !q._isNew) {
          await employeeService.UpdateEmployeeEdu({
            p_heqQualificationId: q.HEQ_QUALIFICATION_ID,
            p_hedEmployeeId: employeeId,
            p_heqQualificationName: q.HEQ_QUALIFICATION_NAME || '',
            p_heqFieldOfStudy: q.HEQ_FIELD_OF_STUDY || '',
            p_heqInstitute: q.HEQ_INSTITUTE || '',
            p_heqResult: q.HEQ_RESULT || '',
            p_heqGrade: q.HEQ_GRADE || '',
            p_heqGPA: q.HEQ_GPA || '',
          });
        } else if (q._isNew && q.HEQ_QUALIFICATION_NAME) {
          await employeeService.AddEmployeeQualification({
            p_hedEmployeeId: employeeId,
            p_heqQualificationTypeId: q.HEQ_QUALIFICATION_TYPE_ID || 1,
            p_heqQualificationName: q.HEQ_QUALIFICATION_NAME || '',
            p_heqFieldOfStudy: q.HEQ_FIELD_OF_STUDY || '',
            p_heqInstitute: q.HEQ_INSTITUTE || '',
            p_heqCountry: 'Sri Lanka',
            p_heqResult: q.HEQ_RESULT || '',
            p_heqGrade: q.HEQ_GRADE || '',
            p_heqGPA: q.HEQ_GPA || '',
            p_heqIsHighest: false,
            p_heqCreatedBy: getLoggedInServiceNo(),
            p_heqIsActive: true,
          });
        }
      }

      // ── 5. Upload Documents ────────────────────────────────────────────────
      const hasFile = files.profilePic || files.nicDocument || files.birthCertificate || files.qualificationCertificate || files.traineeCertificate || files.otherCertificate || files.jobDescription;
      if (hasFile && employeeId) {
        const formData = new FormData();
        if (files.profilePic) formData.append('ProfilePic', files.profilePic);
        if (files.nicDocument) formData.append('NIC', files.nicDocument);
        if (files.birthCertificate) formData.append('BirthCertificate', files.birthCertificate);
        if (files.qualificationCertificate) formData.append('QualificationCertificate', files.qualificationCertificate);
        if (files.traineeCertificate) formData.append('TraineeCertificate', files.traineeCertificate);
        if (files.otherCertificate) formData.append('OtherCertificate', files.otherCertificate);
        if (files.jobDescription) formData.append('JobDescription', files.jobDescription);

        const docRes = await employeeService.UploadEmployeeDocs(employeeId, formData);
        if (docRes?.data && (docRes.data.StatusCode === 400 || docRes.data.StatusCode === 500)) {
          throw new Error(docRes.data.Message || docRes.data.message || 'Document upload failed on the server.');
        }
        clearAuthedFileCache();

        if (files.profilePic) {
          try {
            const base64 = await fileToBase64(files.profilePic);
            const svcNoToSave = serviceNo || employeeId;
            if (svcNoToSave) {
              dispatch(saveEmployeeImage({ serviceNo: String(svcNoToSave).trim(), base64 }));
            }
            if (employeeId && String(employeeId).trim() !== String(svcNoToSave).trim()) {
              dispatch(saveEmployeeImage({ serviceNo: String(employeeId).trim(), base64 }));
            }
          } catch (err) {
            console.error('Failed to convert profile picture to base64 for Redux store:', err);
          }
        }
      }

      if (onSave) await onSave({ ...employee, ...form });

      setDirty(false);
      setLocalSaving(false);
      setLocalSaveError(null);
      onClose();
    } catch (err) {
      setLocalSaving(false);
      const msg = err?.response?.data?.Message || err?.response?.data?.message || err?.message || 'Failed to save changes due to an API error.';
      console.error('Save error:', err);
      setLocalSaveError(msg);
    }
  };

  if (!isOpen || !employee || !form) return null;

  const e = employee;
  const rawSvc = d(e.HED_SERVICE_NO) || d(e.p_ced_service_no) || d(e.HED_EMPLOYEE_ID);
  const uploadedImg = rawSvc ? images[rawSvc] : null;
  const initials = (form.HED_REPORT_NAME || `${form.HED_FIRST_NAME} ${form.HED_LAST_NAME}`)
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '?';
  const directUrl = getEmployeeProfilePicUrl(e);
  const gradient = HEADER_GRADIENT;
  const yearsWorked = calcYears(form.HED_DATE_OF_JOINING);

  // Preview for profile picture: use existing URL if no new file, else use object URL
  const profilePreview = files.profilePic
    ? URL.createObjectURL(files.profilePic)
    : (uploadedImg || directUrl);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => !saving && !localSaving && onClose()}
      />

      <div className="relative bg-gray-50 rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* ── Header ── */}
        <div className={`${gradient} px-6 py-5 shrink-0`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl overflow-hidden shadow-lg ring-2 ring-white/50 shrink-0">
                {profilePreview ? (
                  <img src={profilePreview} alt={initials} className="w-full h-full object-cover" />
                ) : (
                  <div className={`w-full h-full ${gradient} flex items-center justify-center`}>
                    <span className="text-white font-bold text-2xl">{initials}</span>
                  </div>
                )}
              </div>
              <div>
                <h2 className="font-bold text-white text-lg leading-tight">Edit Employee</h2>
                <p className="text-white/80 text-[13px] mt-0.5">
                  {form.HED_FIRST_NAME || form.HED_LAST_NAME
                    ? `${form.HED_FIRST_NAME} ${form.HED_LAST_NAME}`.trim()
                    : (d(e.HED_REPORT_NAME) || d(e.p_report_name) || 'Employee')}
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {rawSvc && (
                    <span className="inline-flex items-center gap-1 bg-white/20 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
                      <Hash size={10} />#{rawSvc}
                    </span>
                  )}
                  {yearsWorked !== null && (
                    <span className="inline-flex items-center gap-1 bg-white/20 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
                      <Clock size={10} />{yearsWorked} yr{yearsWorked !== 1 ? 's' : ''}
                    </span>
                  )}
                  {dirty && (
                    <span className="inline-flex items-center gap-1 bg-amber-400/30 text-amber-100 text-[11px] font-bold px-2 py-0.5 rounded-full">
                      Unsaved changes
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => !saving && !localSaving && onClose()}
              className="text-white/70 hover:text-white hover:bg-white/20 transition-colors p-2 rounded-lg disabled:opacity-40"
              disabled={saving || localSaving}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex border-b border-gray-200 bg-white shrink-0 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-[12px] font-semibold whitespace-nowrap border-b-2 transition-colors
                ${activeTab === key ? 'border-primary-600 text-primary-700 bg-primary-50/60' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            >
              <Icon size={13} className={activeTab === key ? 'text-primary-600' : ''} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Body ── */}
        {/* Fixed height rather than flex-1, matching the view modal: the tabs
            hold very different amounts of content, and without it the dialog
            resized on every tab click. */}
        <div className="overflow-y-auto h-[clamp(360px,58vh,620px)] px-6 py-5 bg-white">
          {(saveError || localSaveError) && (
            <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-[12px]">
              <AlertCircle size={14} className="shrink-0" />
              <span className="flex-1">{saveError || localSaveError}</span>
              <button onClick={() => setLocalSaveError(null)} className="text-red-400 hover:text-red-600">
                <X size={14} />
              </button>
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <div>
                <SH label="Personal Details" icon={User} />
                <div className="grid grid-cols-3 gap-2">
                  <FI label="Title" value={form.HED_TITLE} onChange={set('HED_TITLE')} placeholder="Mr / Ms" />
                  <div className="col-span-2">
                    <FI label="First Name" value={form.HED_FIRST_NAME} onChange={set('HED_FIRST_NAME')} error={errors.HED_FIRST_NAME} />
                  </div>
                </div>
                <FI label="Middle Name" value={form.HED_MIDDLE_NAME} onChange={set('HED_MIDDLE_NAME')} />
                <FI label="Last Name" value={form.HED_LAST_NAME} onChange={set('HED_LAST_NAME')} error={errors.HED_LAST_NAME} />
                <FI label="Report Name" value={form.HED_REPORT_NAME} onChange={set('HED_REPORT_NAME')} placeholder="Display name used in lists" />
                <FI label="Initials" value={form.HED_INITIALS} onChange={set('HED_INITIALS')} />
                <FI label="Gender" value={form.HED_GENDER} onChange={set('HED_GENDER')} options={GENDER_OPTS} />
                <FI label="Date of Birth *" error={errors.HED_DATE_OF_BIRTH}
                  render={(cls) => (
                    <IsoDateInput
                      value={toIso(form.HED_DATE_OF_BIRTH)}
                      onChange={set('HED_DATE_OF_BIRTH')}
                      max={maxDobIso()}
                      className={cls}
                    />
                  )} />
                <FI label="Marital Status" value={form.HED_MARITAL_STATUS} onChange={set('HED_MARITAL_STATUS')} options={MARITAL_OPTS} />
                <FI label="Religion" value={form.HED_RELIGION} onChange={set('HED_RELIGION')} options={RELIGION_OPTS} searchable placeholder="Select Religion" />
                <FI label="Blood Group" value={form.HED_BLOOD_GROUP} onChange={set('HED_BLOOD_GROUP')} options={BLOOD_OPTS} searchable placeholder="Select Blood Group" />
                <FI label="No of Children" value={form.HED_NO_OF_CHILDREN}
                  onChange={(v) => set('HED_NO_OF_CHILDREN')(String(v).replace(/[^0-9]/g, '').slice(0, 2))}
                  mono placeholder="0" />
              </div>
              <div>
                <SH label="Status" icon={Building2} />
                {/* No blank option — an employee is always one or the other,
                    so this doesn't use FI. */}
                <div className="py-1.5">
                  <label className="text-[11px] text-gray-500 font-medium block mb-1">Employment Status</label>
                  <select
                    value={form.HED_IS_ACTIVE ? 'active' : 'inactive'}
                    onChange={(e) => setStatus(e.target.value)}
                    className={`w-full text-[12px] rounded-lg border px-2.5 py-1.5 outline-none transition-colors
                      border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100
                      ${form.HED_IS_ACTIVE ? 'text-emerald-700' : 'text-red-600'}`}
                  >
                    {STATUS_OPTS.map((o) => (
                      <option key={o.value} value={o.value} className="text-gray-700">{o.label}</option>
                    ))}
                  </select>
                </div>

                {!form.HED_IS_ACTIVE && (
                  <>
                    <FI
                      label="Inactive Reason *"
                      value={form.HED_LEAVING_REASON}
                      onChange={set('HED_LEAVING_REASON')}
                      options={INACTIVE_REASON_OPTS}
                      error={errors.HED_LEAVING_REASON}
                    />
                    <FI label="Leaving Date *" error={errors.HED_LEAVING_DATE}
                      render={(cls) => (
                        <IsoDateInput
                          value={form.HED_LEAVING_DATE}
                          onChange={set('HED_LEAVING_DATE')}
                          max={todayIso()}
                          className={cls}
                        />
                      )} />
                  </>
                )}

                <SH label="Identification" icon={Shield} />
                <FI label={tenant?.validation?.nationalIdLabel || 'NIC'} value={form.HED_NIC_NO} onChange={(v) => set('HED_NIC_NO')(sanitiseNic(v))} mono error={errors.HED_NIC_NO} placeholder="200012345678 or 200012345V" />
                <FI label="EPF No. *" value={form.HED_EPF_NO} onChange={(v) => set('HED_EPF_NO')(sanitiseEpf(v))} mono error={errors.HED_EPF_NO} placeholder="max 5" />
                {/* Numbers only — anything else typed or pasted is dropped. */}
                <FI label="TIN No." value={form.HED_TIN_NO} onChange={(v) => { set('HED_TIN_NO')(String(v ?? '').replace(/\D/g, '')); setErrors((er) => ({ ...er, HED_TIN_NO: undefined })); }} mono error={errors.HED_TIN_NO} />
                <FI label="Passport" value={form.HED_PASSPORT_NO} onChange={set('HED_PASSPORT_NO')} mono />

                {/* ── Documents Section ── */}
                <SH label="Documents" icon={Upload} />
                <div className="space-y-3">
                  {/* Profile Picture with preview */}
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="relative shrink-0">
                      <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-md ring-2 ring-primary-100 bg-gray-200 flex items-center justify-center">
                        {profilePreview ? (
                          <img src={profilePreview} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <User size={30} className="text-gray-400" />
                        )}
                      </div>
                      <label
                        htmlFor="edit-profile-pic"
                        className="absolute -bottom-1 -right-1 bg-primary-600 hover:bg-primary-700 text-white p-1.5 rounded-full shadow-lg cursor-pointer transition-transform hover:scale-110"
                        title="Upload Profile Picture"
                      >
                        <Upload size={12} />
                      </label>
                      <input
                        id="edit-profile-pic"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => setFile('profilePic', e.target.files?.[0] || null)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-gray-700">Profile Picture</p>
                      <p className="text-[10px] text-gray-500 truncate">
                        {files.profilePic ? files.profilePic.name : (directUrl ? 'Current photo' : 'No photo')}
                      </p>
                      {files.profilePic && (
                        <button
                          type="button"
                          onClick={() => setFile('profilePic', null)}
                          className="text-[10px] text-red-500 hover:text-red-600 flex items-center gap-1 mt-1"
                        >
                          <Trash2 size={10} /> Remove
                        </button>
                      )}
                    </div>
                  </div>

                  {/* NIC Document */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">NIC Copy</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-nic"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.nicDocument ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.nicDocument ? files.nicDocument.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-nic" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('nicDocument', e.target.files?.[0] || null)} />
                      {files.nicDocument && (
                        <button onClick={() => setFile('nicDocument', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Birth Certificate */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">Birth Certificate</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-birthcert"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.birthCertificate ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.birthCertificate ? files.birthCertificate.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-birthcert" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('birthCertificate', e.target.files?.[0] || null)} />
                      {files.birthCertificate && (
                        <button onClick={() => setFile('birthCertificate', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Qualification Certificate */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">Qualification Certificate</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-qualcert"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.qualificationCertificate ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.qualificationCertificate ? files.qualificationCertificate.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-qualcert" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('qualificationCertificate', e.target.files?.[0] || null)} />
                      {files.qualificationCertificate && (
                        <button onClick={() => setFile('qualificationCertificate', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Trainee Certificate */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">Trainee Certificate</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-traineecert"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.traineeCertificate ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.traineeCertificate ? files.traineeCertificate.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-traineecert" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('traineeCertificate', e.target.files?.[0] || null)} />
                      {files.traineeCertificate && (
                        <button onClick={() => setFile('traineeCertificate', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Other Certificates */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">Other Certificates</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-othercert"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.otherCertificate ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.otherCertificate ? files.otherCertificate.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-othercert" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('otherCertificate', e.target.files?.[0] || null)} />
                      {files.otherCertificate && (
                        <button onClick={() => setFile('otherCertificate', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Job Description */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">Job Description</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-jobdescription"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.jobDescription ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.jobDescription ? files.jobDescription.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-jobdescription" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('jobDescription', e.target.files?.[0] || null)} />
                      {files.jobDescription && (
                        <button onClick={() => setFile('jobDescription', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <div>
                {/* ── Region ──*/}
                <SH label="Region" icon={MapPin} />
                <FI label="Province" render={(cls) => (
                  <SearchSelect className={cls} placeholder="Select Province"
                    value={form.RG_PROVINCE}
                    onChange={(v) => setForm((f) => ({ ...f, RG_PROVINCE: v, RG_DISTRICT: '', RG_ELECTORATE: '' }))}
                    options={withCurrent(
                      rgProvinces.map((r) => ({ value: RG.provCode(r), label: RG.provName(r) || RG.provCode(r) })).filter((o) => o.value),
                      form.RG_PROVINCE, form.RG_PROVINCE_NAME)} />
                )} />
                <FI label="District" render={(cls) => (
                  <SearchSelect className={cls} placeholder={form.RG_PROVINCE ? 'Select District' : 'Select a province first'}
                    value={form.RG_DISTRICT}
                    onChange={(v) => setForm((f) => ({ ...f, RG_DISTRICT: v, RG_ELECTORATE: '' }))}
                    options={withCurrent(
                      rgFilterDistricts(rgDistricts, form.RG_PROVINCE).map((r) => ({ value: RG.distCode(r), label: RG.distName(r) || RG.distCode(r) })).filter((o) => o.value),
                      form.RG_DISTRICT, form.RG_DISTRICT_NAME)} />
                )} />
                <FI label="Electorate" render={(cls) => (
                  <SearchSelect className={cls} placeholder={form.RG_DISTRICT ? 'Select Electorate' : 'Select a district first'}
                    value={form.RG_ELECTORATE}
                    onChange={(v) => setForm((f) => ({ ...f, RG_ELECTORATE: v }))}
                    options={withCurrent(
                      rgFilterElectorates(rgElectorates, form.RG_PROVINCE, form.RG_DISTRICT).map((r) => ({ value: RG.elecCode(r), label: RG.elecArea(r) || RG.elecCode(r) })).filter((o) => o.value),
                      form.RG_ELECTORATE, form.RG_ELECTORATE_NAME)} />
                )} />
                {/* <FI label="Police Station" render={(cls) => (
                  <SearchSelect className={cls} placeholder="Select Police Station" searchable
                    value={form.RG_POLICE_STATION}
                    onChange={set('RG_POLICE_STATION')}
                    options={withCurrent(rgStationOptions(rgStations), form.RG_POLICE_STATION, form.RG_STATION_NAME)} />
                )} /> */}

                <FI label="Police Station" render={(cls) => (
                  <SearchSelect className={cls} placeholder="Select Police Station" searchable
                    value={form.RG_POLICE_STATION}
                    onChange={set('RG_POLICE_STATION')}
                    options={withCurrent(rgStationOptions(rgStations), form.RG_POLICE_STATION, form.RG_STATION_NAME)} />
                )} />

                {/* Flat master list — never narrowed by the four above it. */}
                <FI label="Grama Division" render={(cls) => (
                  <SearchSelect className={cls} placeholder="Select Grama Division"
                    value={form.RG_GRAMA}
                    onChange={set('RG_GRAMA')}
                    options={withCurrent(
                      rgGramas.map((r) => ({ value: RG.gramaCode(r), label: RG.gramaName(r) || RG.gramaCode(r) })).filter((o) => o.value),
                      form.RG_GRAMA, form.RG_GRAMA_NAME)} />
                )} />

                <SH label="Permanent Address" icon={Home} />
                <FI label="Address Line 1" value={form.PERM_LINE1} onChange={set('PERM_LINE1')} error={errors.PERM_LINE1} />
                <FI label="Address Line 2" value={form.PERM_LINE2} onChange={set('PERM_LINE2')} />
                <FI label="Address Line 3" value={form.PERM_LINE3} onChange={set('PERM_LINE3')} />
                <FI label="City" value={form.PERM_CITY} onChange={set('PERM_CITY')} />
                <FI label="Postal Code" value={form.PERM_POSTAL} onChange={(v) => set('PERM_POSTAL')(sanitisePostal(v))} mono />

                <SH label="Current Address" icon={Home} />
                <FI label="Address Line 1" value={form.CURR_LINE1} onChange={set('CURR_LINE1')} />
                <FI label="Address Line 2" value={form.CURR_LINE2} onChange={set('CURR_LINE2')} />
                <FI label="Address Line 3" value={form.CURR_LINE3} onChange={set('CURR_LINE3')} />
                <FI label="City" value={form.CURR_CITY} onChange={set('CURR_CITY')} />
                <FI label="Postal Code" value={form.CURR_POSTAL} onChange={(v) => set('CURR_POSTAL')(sanitisePostal(v))} mono />

                <SH label="Phone Numbers" icon={Phone} />
                <FI label={<>Mobile <span className="text-red-500">*</span></>} value={form.HED_MOBILE_NO} onChange={(v) => set('HED_MOBILE_NO')(sanitisePhone(v))} error={errors.HED_MOBILE_NO} placeholder={tenant?.validation?.phoneExample} />
                <FI label="Home Phone" value={form.HED_HOME_NO} onChange={(v) => set('HED_HOME_NO')(sanitisePhone(v))} error={errors.HED_HOME_NO} placeholder={tenant?.validation?.landlineExample} />
                <FI label="Office Phone" value={form.HED_OFFICE_NO} onChange={(v) => set('HED_OFFICE_NO')(sanitisePhone(v))} error={errors.HED_OFFICE_NO} />

                <SH label="Email" icon={Mail} />
                <FI label="Personal Email" value={form.HED_PERSONAL_EMAIL} onChange={set('HED_PERSONAL_EMAIL')} error={errors.HED_PERSONAL_EMAIL} placeholder={tenant?.validation?.emailExample} />
                <FI label="Company Email" value={form.HED_COMPANY_EMAIL} onChange={set('HED_COMPANY_EMAIL')} error={errors.HED_COMPANY_EMAIL} />

              </div>
              <div>
                <div className="flex items-center justify-between mb-2 mt-4">
                  <div className="flex items-center gap-2">
                    <PhoneCall size={12} className="text-primary-600" />
                    <span className="text-[10px] font-bold text-primary-700 uppercase tracking-wider">Contact &amp; Emergency</span>
                  </div>
                  <button onClick={addContact} type="button" className="flex items-center gap-1 text-[11px] font-semibold text-primary-600 hover:text-primary-800">
                    <Plus size={12} /> Add
                  </button>
                </div>
                {form.Contacts.filter((c) => !c._isDeleted).length === 0 && (
                  <p className="text-[11px] text-gray-400 italic">No contacts. Click Add to create one.</p>
                )}
                {form.Contacts.map((c, i) => {
                  if (c._isDeleted) return null;
                  return (
                    <div key={c._key} className="mb-3 pl-2 border-l-2 border-primary-100 relative">
                      <button onClick={() => removeContact(i)} type="button" className="absolute right-0 top-1 text-gray-300 hover:text-red-500">
                        <Trash2 size={13} />
                      </button>
                      <FI label="Name" value={c.HEC_CONTACT_NAME} onChange={setContact(i, 'HEC_CONTACT_NAME')} />
                      <FI label="Number" value={c.HEC_CONTACT_NUMBER} onChange={(v) => setContact(i, 'HEC_CONTACT_NUMBER')(sanitisePhone(v))} error={errors[`contact_${i}_number`]} />
                      <FI label="Relationship" value={c.HEC_RELATIONSHIP} onChange={setContact(i, 'HEC_RELATIONSHIP')} options={RELATIONSHIP_OPTS} searchable placeholder="Select Relationship" />
                      <FI label="Address" value={c.HEC_ADDRESS} onChange={setContact(i, 'HEC_ADDRESS')} />
                      <label className="flex items-center gap-2 mt-1 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isEmergencyContact(c.HEC_CONTACT_TYPE)}
                          onChange={(ev) => setContact(i, 'HEC_CONTACT_TYPE')(contactTypeFor(ev.target.checked))}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-400"
                        />
                        Set as emergency contact
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'employment' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <div>
                <SH label="Organisation" icon={Building2} />
                <FI
                  label={<>Location <span className="text-red-500">*</span></>}
                  searchable
                  error={errors.HED_LOCATION_ID}
                  value={form.HED_LOCATION_ID || form.HED_COSTCENTER}
                  onChange={(val) => {
                    const matchedLoc = locations.find(l => String(l.value) === String(val));
                    setForm(f => ({
                      ...f,
                      HED_LOCATION_ID: val,
                      HED_COSTCENTER: matchedLoc?.label || val,
                    }));
                    setDirty(true);
                  }}
                  options={locations}
                  loading={employmentLoading}
                  placeholder="Select Location"
                />
                <FI
                  label={<>Division <span className="text-red-500">*</span></>}
                  searchable
                  error={errors.HED_DIV_CODE}
                  value={form.HED_DIV_CODE}
                  onChange={(val) => {
                    // Keep the department only if it belongs to the new division.
                    setForm(f => {
                      const dept = departments.find(d => String(d.value) === String(f.HDD_DEPARTMENT_ID));
                      const keep = dept && dept.div === val;
                      return {
                        ...f,
                        HED_DIV_CODE: val,
                        HDD_DEPARTMENT_ID: keep ? f.HDD_DEPARTMENT_ID : '',
                        HDD_DEPARTMENT_NAME: keep ? f.HDD_DEPARTMENT_NAME : '',
                      };
                    });
                    setErrors(er => ({ ...er, HED_DIV_CODE: undefined }));
                    setDirty(true);
                  }}
                  options={divisions}
                  loading={bankLoading || employmentLoading}
                  placeholder="Select Division"
                />
                {/* Only the chosen division's active departments (HDD_DIV_CODE). */}
                <FI
                  label={<>Department <span className="text-red-500">*</span></>}
                  searchable
                  error={errors.HDD_DEPARTMENT_ID}
                  value={form.HDD_DEPARTMENT_ID || form.HDD_DEPARTMENT_NAME}
                  onChange={(val) => {
                    const matchedDept = departments.find(d => String(d.value) === String(val));
                    setForm(f => ({
                      ...f,
                      HDD_DEPARTMENT_ID: val,
                      HDD_DEPARTMENT_NAME: matchedDept?.label || val,
                    }));
                    setDirty(true);
                  }}
                  options={form.HED_DIV_CODE
                    ? departments.filter(d => d.div === String(form.HED_DIV_CODE).trim())
                    : []}
                  loading={bankLoading || employmentLoading}
                  placeholder={form.HED_DIV_CODE ? 'Select Department' : 'Select Division first'}
                />
                {/* <FI label="Designation" value={form.HED_DESIGNATION_NAME} onChange={set('HED_DESIGNATION_NAME')} /> */}

                <FI
                  label="Designation"
                  searchable
                  value={form.HED_DESIGNATION_ID}
                  onChange={set('HED_DESIGNATION_ID')}
                  options={designations}
                  loading={employmentLoading}
                  placeholder="Select Designation"
                />

                <FI
                  label={<>Grade (Work Category) <span className="text-red-500">*</span></>}
                  searchable
                  error={errors.HED_GRADE_ID}
                  value={form.HED_GRADE_ID}
                  onChange={set('HED_GRADE_ID')}
                  options={grades}
                  loading={employmentLoading}
                  placeholder="Select Grade"
                />

                <FI
                  label={<>Employee Category (Present Employer) <span className="text-red-500">*</span></>}
                  searchable
                  error={errors.HED_EMPLOYMENT_CATEGORY_ID}
                  value={form.HED_EMPLOYMENT_CATEGORY_ID || form.HEC_CATEGORY_NAME}
                  onChange={(val) => {
                    const matchedCat = categories.find(c => String(c.value) === String(val));
                    setForm(f => ({
                      ...f,
                      HED_EMPLOYMENT_CATEGORY_ID: val,
                      HEC_CATEGORY_NAME: matchedCat?.label || val,
                    }));
                    setDirty(true);
                  }}
                  options={categories}
                  loading={employmentLoading}
                  placeholder="Select Category"
                />

                <FI
                  label={<>Shift <span className="text-red-500">*</span></>}
                  searchable
                  error={errors.HED_SHIFT_ID}
                  value={form.HED_SHIFT_ID}
                  onChange={set('HED_SHIFT_ID')}
                  options={shifts}
                  loading={employmentLoading}
                  placeholder="Select Shift"
                />

                <FI
                  label="Employee Type"
                  value={form.HED_EMPLOYEE_TYPE}
                  onChange={set('HED_EMPLOYEE_TYPE')}
                  options={EMPLOYEE_TYPES}
                  placeholder="Select Employee Type"
                />

                <FI
                  label="OT Type"
                  value={form.HED_OT_TYPE}
                  onChange={set('HED_OT_TYPE')}
                  options={OT_TYPES}
                  placeholder="Select OT Type"
                />

                <FI
                  label="Reporting Employee"
                  searchable
                  value={form.HED_REPORTING_EMPLOYEE_ID}
                  onChange={set('HED_REPORTING_EMPLOYEE_ID')}
                  options={reportingOptions}
                  loading={employmentLoading}
                  placeholder="Select Reporting Employee"
                />

                <FI label="Notice Period (days)" value={form.HED_NOTICE_PERIOD_DAYS} onChange={set('HED_NOTICE_PERIOD_DAYS')} type="number" />
                <FI label="OT Rate (LKR)" value={form.HED_OT_RATE} onChange={set('HED_OT_RATE')} type="number" />
                <FI label="Total Experience" error={errors.HED_TOTAL_EXPERIANCE}
                  render={(cls) => (
                    <ExperienceInput
                      value={form.HED_TOTAL_EXPERIANCE}
                      onChange={set('HED_TOTAL_EXPERIANCE')}
                      inputClassName={cls}
                    />
                  )} />
              </div>
              <div>
                <SH label="Key Dates" icon={Calendar} />
                <FI label="Join Date" error={errors.HED_DATE_OF_JOINING}
                  render={(cls) => (
                    <IsoDateInput
                      value={toIso(form.HED_DATE_OF_JOINING)}
                      onChange={set('HED_DATE_OF_JOINING')}
                      max={todayIso()}
                      className={cls}
                    />
                  )} />
                {/* <FI label="Confirmation" value={form.HED_CONFIRMATION_DATE} onChange={set('HED_CONFIRMATION_DATE')} type="date" /> */}
                {/* Contract staff: Termination Date only, the four career dates
                    are hidden. */}
                {isContractForm(form, categories) ? (
                <FI label={<>Termination Date <span className="text-red-500">*</span></>}
                  error={errors.HED_TERMINATION_DATE}
                  render={(cls) => (
                    <IsoDateInput
                      value={toIso(form.HED_TERMINATION_DATE)}
                      onChange={(v) => {
                        set('HED_TERMINATION_DATE')(v);
                        setErrors((er) => ({ ...er, HED_TERMINATION_DATE: undefined }));
                      }}
                      className={cls}
                    />
                  )} />
                ) : (<>
                {/* Only recruitment is capped at today — promotion, permanency
                    and retirement can all legitimately be future dates. */}
                <FI label="Recruitment Date"
                  render={(cls) => (
                    <IsoDateInput
                      value={toIso(form.HED_RECRUITMENT_DATE)}
                      onChange={set('HED_RECRUITMENT_DATE')}
                      max={todayIso()}
                      className={cls}
                    />
                  )} />
                <FI label="Promoted Date"
                  render={(cls) => (
                    <IsoDateInput
                      value={toIso(form.HED_PROMOTED_DATE)}
                      onChange={set('HED_PROMOTED_DATE')}
                      className={cls}
                    />
                  )} />
                <FI label="Permanent Date"
                  render={(cls) => (
                    <IsoDateInput
                      value={toIso(form.HED_PERMANENT_DATE)}
                      onChange={set('HED_PERMANENT_DATE')}
                      className={cls}
                    />
                  )} />
                <FI label="Retirement Date"
                  render={(cls) => (
                    <IsoDateInput
                      value={toIso(form.HED_RETIREMENT_DATE)}
                      onChange={set('HED_RETIREMENT_DATE')}
                      className={cls}
                    />
                  )} />
                </>)}

                <SH label="Notes" icon={Briefcase} />
                <div className="py-1.5">
                  <label className="text-[11px] text-gray-500 font-medium block mb-1">Remarks</label>
                  <textarea
                    className="w-full text-[12px] rounded-lg border border-gray-200 px-2.5 py-1.5 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 min-h-[80px]"
                    value={form.HED_REMARKS}
                    onChange={(e) => set('HED_REMARKS')(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bank' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={addBank} type="button" className="flex items-center gap-1 text-[11px] font-semibold text-primary-600 hover:text-primary-800">
                  <Plus size={12} /> Add bank account
                </button>
              </div>
              {form.Banks.filter((b) => !b._isDeleted).length === 0 && (
                <p className="text-[12px] text-gray-400 italic">No bank accounts on record.</p>
              )}
              {errors.BANK_PRIMARY && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-[12px] text-red-700">
                  <AlertCircle size={13} className="shrink-0" /> {errors.BANK_PRIMARY}
                </div>
              )}
              {form.Banks.map((b, i) => {
                if (b._isDeleted) return null;
                return (
                  <div key={b._key}
                    className={`p-4 rounded-xl border relative transition-colors
                      ${b._isActive === false ? 'border-gray-200 bg-gray-100/70' : 'border-gray-100 bg-gray-50'}`}>
                    <button onClick={() => removeBank(i)} type="button" className="absolute right-3 top-3 text-gray-300 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard size={14} className="text-primary-500" />
                      <span className="text-[12px] font-bold text-gray-700">Bank Account {i + 1}</span>
                      <label className="flex items-center gap-1.5 ml-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!!b.HEB_IS_PRIMARY}
                          onChange={(e) => {
                            const on = e.target.checked;
                            // Only one primary: ticking this one unticks the rest
                            // (each unticked account is saved with p_hebIsPrimary = false).
                            setForm((f) => ({
                              ...f,
                              Banks: f.Banks.map((bk, j) =>
                                j === i ? { ...bk, HEB_IS_PRIMARY: on }
                                  : on && !bk._isDeleted ? { ...bk, HEB_IS_PRIMARY: false } : bk),
                            }));
                            setErrors((er) => ({ ...er, BANK_PRIMARY: undefined }));
                            setDirty(true);
                          }}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-400"
                        />
                        <span className="text-[11px] text-gray-500">Primary</span>
                      </label>
                      {/* Only saved accounts can be switched off; a new one is always active. */}
                      {!b._isNew && (
                        <label className="flex items-center gap-1.5 ml-2 cursor-pointer select-none"
                          title="Untick to make this account inactive">
                          <input
                            type="checkbox"
                            checked={b._isActive !== false}
                            onChange={(e) => setBank(i, '_isActive')(e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-400"
                          />
                          <span className="text-[11px] text-gray-500">Active</span>
                        </label>
                      )}
                      {b._isActive === false && (
                        <span className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                      <div>
                        <FI label="Bank" value={b.HBD_BANK_NAME} onChange={setBank(i, 'HBD_BANK_NAME')} />
                        <FI label="Branch" value={b.HBB_BRANCH_NAME} onChange={setBank(i, 'HBB_BRANCH_NAME')} />
                      </div>
                      <div>
                        <FI label="Account Name" value={b.HEB_ACCOUNT_NAME} onChange={setBank(i, 'HEB_ACCOUNT_NAME')} />
                        <FI label="Account Number" value={b.HEB_ACCOUNT_NUMBER} onChange={setBank(i, 'HEB_ACCOUNT_NUMBER')} mono />
                        <FI label="Account Type" value={b.HEB_ACCOUNT_TYPE} onChange={setBank(i, 'HEB_ACCOUNT_TYPE')} />
                      </div>
                    </div> */}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                      <div>
                        <FI
                          label="Bank"
                          searchable
                          placeholder="Select Bank"
                          value={b.HBD_BANK_ID}
                          onChange={(value) => {
                            setForm((f) => {
                              const list = [...f.Banks];

                              list[i] = {
                                ...list[i],
                                HBD_BANK_ID: value,
                                HBB_BRANCH_ID: '',
                              };

                              return {
                                ...f,
                                Banks: list,
                              };
                            });

                            setDirty(true);
                          }}
                          options={banks}
                        />

                        <FI
                          label="Branch"
                          searchable
                          placeholder={b.HBD_BANK_ID ? 'Select Branch' : 'Select a bank first'}
                          value={b.HBB_BRANCH_ID}
                          onChange={setBank(i, 'HBB_BRANCH_ID')}
                          options={
                            b.HBD_BANK_ID
                              ? branches.filter(
                                  (branch) => branch.bankId === String(b.HBD_BANK_ID)
                                )
                              : []
                          }
                        />
                      </div>

                      <div>
                        <FI
                          label="Account Name"
                          value={b.HEB_ACCOUNT_NAME}
                          onChange={setBank(i, 'HEB_ACCOUNT_NAME')}
                        />

                        <FI
                          label="Account Number"
                          value={b.HEB_ACCOUNT_NUMBER}
                          onChange={setBank(i, 'HEB_ACCOUNT_NUMBER')}
                          mono
                        />

                        <FI
                          label="Account Type"
                          value={b.HEB_ACCOUNT_TYPE}
                          onChange={setBank(i, 'HEB_ACCOUNT_TYPE')}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'qualify' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={addQualification} type="button" className="flex items-center gap-1 text-[11px] font-semibold text-primary-600 hover:text-primary-800">
                  <Plus size={12} /> Add Qualification
                </button>
              </div>
              {form.Qualifications.filter((q) => !q._isDeleted).length === 0 && (
                <p className="text-[12px] text-gray-400 italic">No qualifications on record.</p>
              )}
              {form.Qualifications.map((q, i) => {
                if (q._isDeleted) return null;
                return (
                  <div key={q._key} className="p-4 rounded-xl border border-gray-100 bg-gray-50 relative">
                    <button onClick={() => removeQualification(i)} type="button" className="absolute right-3 top-3 text-gray-300 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                    <div className="flex items-center gap-2 mb-3">
                      <Award size={14} className="text-primary-500" />
                      <span className="text-[12px] font-bold text-gray-700">Qualification {i + 1}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                      <div>
                        <FI label="Qualification Name" value={q.HEQ_QUALIFICATION_NAME} onChange={setQualification(i, 'HEQ_QUALIFICATION_NAME')} />
                        <FI label="Field of Study" value={q.HEQ_FIELD_OF_STUDY} onChange={setQualification(i, 'HEQ_FIELD_OF_STUDY')} />
                        <FI label="Institute" value={q.HEQ_INSTITUTE} onChange={setQualification(i, 'HEQ_INSTITUTE')} />
                      </div>
                      <div>
                        <FI label="Result" value={q.HEQ_RESULT} onChange={setQualification(i, 'HEQ_RESULT')} />
                        <FI label="Grade" value={q.HEQ_GRADE} onChange={setQualification(i, 'HEQ_GRADE')} />
                        <FI label="GPA" value={q.HEQ_GPA} onChange={setQualification(i, 'HEQ_GPA')} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="shrink-0 bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-between rounded-b-xl">
          <p className="text-[10px] text-gray-400">
            Press <kbd className="px-1 py-0.5 rounded border border-gray-300 font-mono text-[10px] bg-white">ESC</kbd> to close
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => !saving && !localSaving && onClose()}
              disabled={saving || localSaving}
              className="h-9 px-4 text-xs font-semibold rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || localSaving}
              className="flex items-center gap-2 h-9 px-5 text-xs font-semibold rounded-lg bg-primary-600 hover:bg-primary-700 text-white shadow-sm transition-colors disabled:opacity-50"
            >
              {saving || localSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving || localSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}